
/* istanbul ignore file */

import embLabels from "../../../config/emb-labels-en.json";
import ACTIONS from "../Design_Asset_Actions.json";
import { isBetween, isValidCBName, isValidName, padNumberFormat } from "./designAssetUtils";
import { generateRandom } from "../../../utils/randomGenerator";
import _ from "lodash";

const alphanumericRegWithHyphen = /^[-_0-9a-zA-Z]+$/;

const useAssetConfigHandlers = (deps) => {
  const {
    projectTypeValue, dispatch, UserScope, projectType,
    loading, setLoading,
    displayPage, setDisplayPage,
    sldLoadCompleted, setSldLoadCompleted,
    useCaseValidGenset, setUseCaseValidGenset,
    deleteOneDER, setDeleteOneDER,
    defaultConfigurationStatus, setDefaultConfigurationStatus,
    siteCreated, setSiteCreated,
    isOpen, setIsOpen,
    siteType, setSiteType,
    evceDiagramChangeRequired, setEvceDiagramChangeRequired,
    isAllowMultipleLoads, setIsAllowMultipleLoads,
    isExportOptimization, setIsExportOptimization,
    switchboardType, setSwitchboardType,
    clientType, setClientType,
    architectureType, setArchitectureType,
    nominalVoltage, setNominalVoltage,
    isSelectSpanishCode, setIsSelectSpanishCode,
    defaultPreFillData, setDefaultPreFillData,
    availableDer, setAvailableDer,
    useCases, setUseCases,
    isEvce, setIsEvce,
    prevPvGroupNames, setPrevPvGroupNames,
    batteryCommunicationData, setBatteryCommunicationData,
    evCommunicationData, setEvCommunicationData,
    powerMeter, setPowerMeter,
    communicationState, setCommunicationState,
    cbCommunicationValues, setCbCommunicationValues,
    detailsBlock, setDetailsBlock,
    selectedValues, setSelectedValues,
    configData, setConfigData,
    isLoadNameUnique, setIsLoadNameUnique,
    gsxExternalId, setGsxExternalId,
    isBessController, setIsBessController,
    assetDetailsDisplay, setAssetDetailsDisplay,
    assetInfo, setAssetInfo,
    bessControllerPrevValue, setBessControllerPrevValue,
    cbDer, setCbder,
    CBValues, setCBValues,
    defaultsMinMax, setDefaultsMinMax,
    coordinates, setCoordinates,
    rectangleWidth, setRectangleWidth,
    snackBar, setSnackBar,
    alert, setAlert,
    confirm, setConfirm,
    remoteConfirm, setRemoteConfirm,
    duplicateUpdate, setDuplicateUpdate,
    runCalculateCoordinates, setRunCalculateCoordinates,
    runGenerateComponentsBlock, setRunGenerateComponentsBlock,
    batteryRack, setBatteryRack,
    batterySystemA2EConfig, setBatterySystemA2EConfig,
    isOpenLoadConfig, setIsOpenLoadConfig,
    isOpenGtConfig, setIsOpenGtConfig,
    loadSide, setLoadSide,
    isAntiIslandingRelayEnable, setIsAntiIslandingRelayEnable,
    gridTransitionOnline, setGridTransitionOnline,
    defaultCbData, setDefaultCbData,
    defaultPmData, setDefaultPmData,
    defaultDummyCbData, setDefaultDummyCbData,
    gensetAssetData, setGensetAssetData,
    evContactorData, setEVContactorData,
    gTContractorData, setGTContractorData,
    defaultGTData, setGTDefaultData,
    evDefaultData, setEVDefaultData,
    evPMData, setEVPMData,
    jsonData, setJsonData,
    isContactorEnabled, setIsContactorEnabled,
    cbLoadData, setCBLoadData,
    currentProject, setCurrentProject,
    defaultComponentData, setDefaultComponentData,
    updateComponents, setUpdateComponents,
    cbCount, setCbCount,
    spanishCodeRealTime, setSpanishCodeRealTime,
    assetDefaultValues, setAssetDefaultValues,
    isAssetConfigChanged, setIsAssetConfigChanged,
    gtTempConfigData, setGtTempConfigData,
    projectStatusData,
    deploymentReady, setDeploymentReady,
    ComponentData, ComponentDataDispatch,
    CBAssetList, CBAssetListDispatch,
    CBEVList, CBEVListDispatch,
    CBLoadList, CBLoadListDispatch,
    CBGTList, CBGTListDispatch,
    firstTimeCall, setFirstTimecall,
    multipleLoads, setMultipleLoads,
    count, countDispatch,
    projectKind, projectKindDispatch,
    PVDetails, PVDetailsDispatch,
    selectedLoadIndx, setSelectedLoadIndx,
    projectId,
    initialRenderCoordinates,
    initialRenderPvConfigured,
    snackBarRef,
    isUpdatingComponent,
    remoteMain, setRemoteMain,
    considerSelected, setConsiderSelected,
    assetDetails, setAssetDetails,
    // projectKind flags
    isMgaasAnsi, isDaasAnsi, isDaasEto, isMgaasIec, isDaasIec,
    isMgaasAnsiGrid, isDaasAnsiGrid, isDaasEtoGrid,
    isMgaasAnsiIsland, isDaasAnsiIsland, isDaasEtoIsland,
    isMgaasIecGrid, isDaasIecGrid, isMgaasIecIsland,
    isGridConnected, isGridConnectedGSX, isIslandable, isIslandableGSX,
    isA2E, isLineSide, isExistingSwitchBoard, isLoadSide, isNewSwitchBoard,
    isGreenStruxure, isGeneric, isGreenStruxureMicrogrid,
    isPVGensetATS, isFuelSaver, isPVBessNR, isPVHybrid, isPVGensetA2E,
    isGensetMainNoBess, isNoFuel, isETO,
    isMGaaSETOGrid, isMGaaSETOIsland, isMGaaSETO, isMGaaSETOGensetPvHybrid,
    isNMGaaSETOGrid, isNMGaaSETOIsland, isNMGaaSETO, isNMGaaSETOGensetPvHybrid,
    isMgaasAnsiOffGrid, isMgaasIecOffGrid, isMassEtoOffGrid,
    // cross-hook ref
    fnRef,
  } = deps;

  // Cross-hook function aliases
  const callSnackbar = (...args) => fnRef.current.callSnackbar(...args);
  const generateComponentsBlock = (...args) => fnRef.current.generateComponentsBlock(...args);
  const calculateCoOridinates = (...args) => fnRef.current.calculateCoOridinates(...args);
  const checkForDuplicateName = (...args) => fnRef.current.checkForDuplicateName(...args);
  const setAlertMessage = (...args) => fnRef.current.setAlertMessage(...args);
  const selectedValuesHandler = (...args) => fnRef.current.selectedValuesHandler(...args);
  const selectedSubPvHandler = (...args) => fnRef.current.selectedSubPvHandler(...args);
  const assetDetailsReset = (...args) => fnRef.current.assetDetailsReset(...args);
  const selectedAssetHandlerPv = (...args) => fnRef.current.selectedAssetHandlerPv(...args);
  const updateDerHistory = (...args) => fnRef.current.updateDerHistory(...args);
  const updateDerStatus = (...args) => fnRef.current.updateDerStatus(...args);
  const updatePvHistory = (...args) => fnRef.current.updatePvHistory(...args);
  const updatePvHistoryGroupPower = (...args) => fnRef.current.updatePvHistoryGroupPower(...args);
  const updateLoadHistory = (...args) => fnRef.current.updateLoadHistory(...args);
  const updateAssetCbHistory = (...args) => fnRef.current.updateAssetCbHistory(...args);
  const updateLoadCbHistory = (...args) => fnRef.current.updateLoadCbHistory(...args);
  const updateAssetPmHistory = (...args) => fnRef.current.updateAssetPmHistory(...args);
  const updateEvPmHistory = (...args) => fnRef.current.updateEvPmHistory(...args);
  const assetEditStatusUpdateDER = (...args) => fnRef.current.assetEditStatusUpdateDER(...args);
  const assetEditStatusUpdateLoad = (...args) => fnRef.current.assetEditStatusUpdateLoad(...args);
  const checkAssetStatus = (...args) => fnRef.current.checkAssetStatus(...args);

  const assetConfigDataHandler = (index) => {
    const CBASSETLISTNEW = _.cloneDeep(CBAssetList);
    const defaultsData = {};
    for (let defaultData of defaultPreFillData[2]) {
      defaultsData[defaultData["param"]] = defaultData["value"];
    }

    if (CBASSETLISTNEW[index]?.configData?.name) {
      let assetConfigDataNew = {};
      assetConfigDataNew = CBASSETLISTNEW[index]?.configData;
      if (CBASSETLISTNEW[index]?.assetType === embLabels.UTILITY) {
        assetConfigDataNew = Object.assign({}, configData?.utilityConfigData);
        assetConfigDataNew = {
          ...CBASSETLISTNEW[index]?.configData,

        }
        assetConfigDataHandler.maximum_available_power = 300;

      } else if (CBASSETLISTNEW[index]?.assetType === embLabels.GENSET) {
        assetConfigDataNew = Object.assign({}, configData?.gensetConfigData);
        // assetConfigDataNew.max_power = defaultsData.maxPowerGensetDefault;
        assetConfigDataNew = {
          ...CBASSETLISTNEW[index]?.configData,

        }
      } else if (CBASSETLISTNEW[index]?.assetType === embLabels.BESS) {
        assetConfigDataNew = Object.assign({}, configData?.batteryConfigData);
        assetConfigDataNew = {
          ...CBASSETLISTNEW[index]?.configData,

        }
        assetConfigDataNew.soc_max = assetConfigDataNew.soc_max ?? defaultPreFillData[0]?.BESS?.soc_max;
        assetConfigDataNew.soc_min = assetConfigDataNew.soc_min ?? defaultPreFillData[0]?.BESS?.soc_min;
        assetConfigDataNew.SoC_min_optimization = assetConfigDataNew.SoC_min_optimization ?? defaultsData?.socMinOptimizationDefault;
        assetConfigDataNew.soc_nominal_power = CBASSETLISTNEW[index]?.configData?.soc_nominal_power ?? defaultsData?.socNominalDefault;
        assetConfigDataNew.SoC_fallback = assetConfigDataNew?.SoC_fallback ?? defaultsData?.SoC_fallback;

      } else if (CBASSETLISTNEW[index].assetType === embLabels.CHP) {
        assetConfigDataNew = Object.assign({}, configData?.chpConfigData);
        assetConfigDataNew = {
          ...CBASSETLISTNEW[index]?.configData,

        }
      } else if (CBASSETLISTNEW[index].assetType === embLabels.HYBRID) {
        assetConfigDataNew = Object.assign({}, configData?.hybridConfigData);
        assetConfigDataNew = {
          ...CBASSETLISTNEW[index]?.configData,

        }
      } else {
        assetConfigDataNew = Object.assign({}, configData?.sampleConfigData);
        assetConfigDataNew = {
          ...CBASSETLISTNEW[index]?.configData,

        }
      }

      
      assetConfigDataNew.name = CBASSETLISTNEW[index]?.configData?.name;
      assetConfigDataNew.description =
        CBASSETLISTNEW[index]?.configData?.description;
      // assetConfigDataNew.max_power=CBASSETLISTNEW[1].
      if (CBASSETLISTNEW[index]?.assetType === embLabels.GENSET) {
        CBASSETLISTNEW[1]?.selectedAssetData?.assetAttributes &&
          CBASSETLISTNEW[1]?.selectedAssetData?.assetAttributes.forEach(
            (assetAttribute) => {
              if (assetAttribute?.type === "gensetPower") {
                assetConfigDataNew.max_power = assetAttribute?.value;
              }
            }
          );
        // assetConfigDataNew.max_power = defaultsData.maxPowerGensetDefault;
      }
      // assetConfigDataNew.max_power =
      //   CBASSETLISTNEW[index].configData.maximum_available_power ||
      //   CBASSETLISTNEW[index].configData.max_power;
      // if (CBASSETLISTNEW[index].assetType === embLabels.BESS){
      //   assetConfigDataNew.capacity = CBAssetList[index].assetDetails[0]?.assetAttributes.find(
      //     attribute => attribute.type === "capacity"
      //   )?.value;
      //   assetConfigDataNew.max_power =  CBAssetList[index].assetDetails[0]?.assetAttributes.find(
      //     attribute => attribute.type === "power_max"
      //   )?.value;
      // }
      // if (CBASSETLISTNEW[index].assetType === embLabels.GENSET){
      //   assetConfigDataNew.max_power =  CBAssetList[index].assetDetails[0]?.assetAttributes.find(
      //     attribute => attribute.type === "min_power"
      //   )?.value;
      // }
      // if(siteType === 'GENSET_PVBESS_A2E'){
      //   assetConfigDataNew.max_power = CBAssetList[7].assetDetails[0]?.assetAttributes.find(
      //     attribute => attribute.type === "minGensetPower"
      //   )?.value;
      // }
      //  assetConfigDataNew.max_power = defaultsData.max_power;

      const derHistory = JSON.parse(sessionStorage.getItem("derHistory"));

      if (index === 0) {
        Object.entries(derHistory[embLabels.UTILITY]).forEach(
          ([key, value]) => {
            assetConfigDataNew[key] = value;
          }
        );
      } else if (index === 1) {
        Object.entries(derHistory[embLabels.GENSET]).forEach(([key, value]) => {
          assetConfigDataNew[key] = value;
        });
      } else if (index === 2) {
        Object.entries(derHistory[embLabels.BESS]).forEach(([key, value]) => {
          assetConfigDataNew[key] = value;
        });
      } else if (index === 6) {
        Object.entries(derHistory[embLabels.CHP]).forEach(([key, value]) => {
          assetConfigDataNew[key] = value;
        });
      } else if (index === 7) {
        Object.entries(derHistory[embLabels.HYBRID]).forEach(([key, value]) => {
          assetConfigDataNew[key] = value;
        });
      }

      setConfigData((prevState) => ({
        ...prevState,
        configData: assetConfigDataNew,
      }));
    } else {
      const assetConfigDataNew = { ...configData?.sampleConfigData };
      assetConfigDataNew.name = "";
      assetConfigDataNew.description = "";
      setConfigData((prevState) => ({
        ...prevState,
        configData: assetConfigDataNew,
      }));
    }
  };

  const loadConfigDataHandler = (index, flag) => {
    
    if(flag !== "skip_this"){
      setSelectedLoadIndx(index);
    }
    
    const loadArray = [...CBLoadList];
    const CBASSETLISTNEW = _.cloneDeep(CBAssetList);
    if (loadArray[index]?.configData?.name) {
      let cbConfigDataNew = { ...configData?.loadConfigData };
      cbConfigDataNew.name = loadArray[index]?.configData?.name;
      cbConfigDataNew.description = loadArray[index]?.configData?.description;
      cbConfigDataNew.maximum_available_power = loadArray[index].configData.maximum_available_power;
      CBASSETLISTNEW[1].selectedAssetData.assetAttributes &&
        CBASSETLISTNEW[1].selectedAssetData.assetAttributes.forEach(
          (assetAttribute) => {
            if (assetAttribute?.type === "loadPower"&&assetAttribute?.value!=undefined) {
              cbConfigDataNew.maximum_available_power = assetAttribute.value;
              CBLoadListDispatch({
                type: ACTIONS.UPDATE_MAXIMUM_LOAD_POWER,
                payload: {
                  index: 0,
                  loadPower: assetAttribute.value,
                },
              });
            }
          }
        );

      let derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let loadHistory = [];
      if (derStatus[4] === true) {
        loadHistory = JSON.parse(sessionStorage.getItem("loadHistory"));
        if (loadHistory[index] !== undefined && loadHistory[index] !== null) {
          Object.entries(loadHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }

      setConfigData((prevState) => ({
        ...prevState,
        configData: cbConfigDataNew,
      }));
    } else {
      let cbConfigDataNew = { ...configData?.loadConfigData };
      cbConfigDataNew.name = "";
      cbConfigDataNew.description = "";
      cbConfigDataNew.maximum_available_power = 0;
      setConfigData((prevState) => ({
        ...prevState,
        configData: cbConfigDataNew,
      }));
    }
  };
   const gtConfigDataHandler = (index) => {
    if (CBGTList[index].configData.name) {
      let gtConfigDataNew = { ...configData.gtConfigData };
      gtConfigDataNew = CBGTList[index]?.configData;
      gtConfigDataNew.name = CBGTList[index]?.configData?.name;
      gtConfigDataNew.description = CBGTList[index]?.configData?.description;
      let derHistory = JSON.parse(sessionStorage.getItem("derHistory"));

      Object.entries(derHistory["GT"]).forEach(([key, value]) => {
        gtConfigDataNew[key] = value;
      });
      setConfigData((prevState) => ({
        ...prevState,
         gtConfigData: gtConfigDataNew,
      }));
    } else {
      const gtConfigDataNew = { ...configData.gtConfigData };
      gtConfigDataNew.name = "";
      gtConfigDataNew.description = "";
      setConfigData((prevState) => ({
        ...prevState,
         gtConfigData: gtConfigDataNew,
      }));
    }
  };

  const evConfigDataHandler = (index) => {
    if (CBEVList?.[index]?.configData?.name) {
      let evConfigDataNew = { ...configData?.evConfigData };
      evConfigDataNew = CBEVList?.[index]?.configData;
      evConfigDataNew.name = CBEVList?.[index]?.configData?.name;
      evConfigDataNew.description = CBEVList?.[index]?.configData?.description;
      evConfigDataNew.maximum_available_power =
        CBEVList?.[index]?.configData?.maximum_available_power;

      let derHistory = JSON.parse(sessionStorage.getItem("derHistory"));

      Object.entries(derHistory["EV"]).forEach(([key, value]) => {
        evConfigDataNew[key] = value;
      });

      setConfigData((prevState) => ({
        ...prevState,
        configData: evConfigDataNew,
      }));
    } else {
      const evConfigDataNew = { ...configData?.evConfigData };
      evConfigDataNew.name = "";
      evConfigDataNew.description = "";
      setConfigData((prevState) => ({
        ...prevState,
        configData: evConfigDataNew,
      }));
    }
  };
 


  //* GET CB CONFIG DATA

  const cbConfigDataHandler = (index) => {
    const CBASSETLISTNEW = _.cloneDeep(CBAssetList);
    if (CBASSETLISTNEW?.[index]) {
      const cbConfigDataNew = { ...configData?.sampleCbConfigData };
      if (siteType === embLabels.PV_HYBRIDIZATION) {
        cbConfigDataNew.name = CBASSETLISTNEW?.[index]?.cbConfigData?.name;
        cbConfigDataNew.description =
          CBASSETLISTNEW?.[index]?.cbConfigData?.description;
      } else if (
        [
          embLabels.ISLANDABLE,
          embLabels.GRID_CONNECTED,
          embLabels.OFF_GRID,
          embLabels.GENSET_PVBESS_A2E,
        ].includes(siteType)
      ) {
        cbConfigDataNew.name = CBASSETLISTNEW?.[index]?.cbConfigData?.name;
        cbConfigDataNew.description =
          CBASSETLISTNEW[index]?.cbConfigData?.description;
        cbConfigDataNew.motorized =
          CBASSETLISTNEW[index]?.cbConfigData?.motorized;
          cbConfigDataNew.contactor =
          CBASSETLISTNEW[index]?.cbConfigData?.contactor;
      } else {
        //* do nothing
      }

      const derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let cbAssetHistory = [];
      if (derStatus[6]) {
        if (sessionStorage.getItem("cbAssetHistory") === "") {
          cbAssetHistory = [];
        } else {
          cbAssetHistory = JSON.parse(sessionStorage.getItem("cbAssetHistory"));
        }
        if (
          cbAssetHistory[index] !== undefined &&
          cbAssetHistory[index] !== null
        ) {
          Object.entries(cbAssetHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }
      const newcontactorConfigData = {
        ...configData?.sampleContactorConfigData,
      };
      if ([embLabels.GRID_CONNECTED,embLabels.ISLANDABLE, embLabels.OFF_GRID].includes(siteType)) {
        newcontactorConfigData.name =
          CBASSETLISTNEW[index]?.contactorConfigData?.name;
        newcontactorConfigData.description =
          CBASSETLISTNEW[index]?.contactorConfigData?.description;
      }

      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
        contactorConfigData: newcontactorConfigData,
      }));
    } else {
      const cbConfigDataNew = { ...configData?.sampleCbConfigData };
      if (siteType === embLabels.PV_HYBRIDIZATION) {
        cbConfigDataNew.name = "";
        cbConfigDataNew.description = "";
      } else if (
        [
          embLabels.ISLANDABLE,
          embLabels.OFF_GRID,
          embLabels.GRID_CONNECTED,
          embLabels.OFF_GRID,
        ].includes(siteType)
      ) {
        cbConfigDataNew.name = "";
        cbConfigDataNew.description = "";
        cbConfigDataNew.motorized = true;
        cbConfigDataNew.contactor = false;
      } else {
        //* do nothing
      }
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    }
  };

  const newSBCbConfigDataHandler = (index) => {
    const CBASSETLISTNEW = _.cloneDeep(CBAssetList);
    if (CBASSETLISTNEW[index]) {
      const cbConfigDataNew = { ...configData?.sampleCbConfigData };

      cbConfigDataNew.name = CBASSETLISTNEW[index]?.cbConfigData?.name;
      cbConfigDataNew.description =
        CBASSETLISTNEW[index]?.cbConfigData?.description;
      cbConfigDataNew.motorized =
        CBASSETLISTNEW[index]?.cbConfigData?.motorized;
      cbConfigDataNew.contactor =
        CBASSETLISTNEW[index]?.cbConfigData?.contactor;

      const derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let cbAssetHistory = [];
      if (derStatus[6]) {
        if (sessionStorage.getItem("cbAssetHistory") === "") {
          cbAssetHistory = [];
        } else {
          cbAssetHistory = JSON.parse(sessionStorage.getItem("cbAssetHistory"));
        }
        if (
          cbAssetHistory[index] !== undefined &&
          cbAssetHistory[index] !== null
        ) {
          Object.entries(cbAssetHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }

      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    } else {
      const cbConfigDataNew = { ...configData?.sampleCbConfigData };

      cbConfigDataNew.name = "";
      cbConfigDataNew.description = "";
      cbConfigDataNew.motorized = true;
      cbConfigDataNew.contactor = false;

      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    }
  };

  const loadCbConfigDataHandler = (index) => {
   
    const loadArray = [...CBLoadList];
    const assetDetailsData = { ...assetDetailsDisplay };
    if (loadArray[index]) {
      let cbConfigDataNew = { ...configData?.cbConfigData };
      cbConfigDataNew.name = loadArray[index]?.cbConfigData?.name;
      cbConfigDataNew.description = loadArray[index]?.cbConfigData?.description;
      cbConfigDataNew.motorized = loadArray[index]?.cbConfigData?.motorized;

      let derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let cbLoadHistory = [];
      if (derStatus[6] === true) {
        if (sessionStorage.getItem("cbLoadHistory") === "") {
          cbLoadHistory = [];
        } else {
          cbLoadHistory = JSON.parse(sessionStorage.getItem("cbLoadHistory"));
        }

        if (
          cbLoadHistory[index] !== undefined &&
          cbLoadHistory[index] !== null
        ) {
          Object.entries(cbLoadHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }
      if(!projectKind?.isA2E){
      setAssetDetailsDisplay({
        ...assetDetailsData,
        communicationModel:
          CBLoadList[index]?.cbConfigData?.cbAssetDetails?.[0]
            ?.communicationModel,
      });
    }
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
        contactorConfigData: CBLoadList?.[index]?.contactorConfigData,
      }));
    } else {
      let cbConfigDataNew = { ...configData?.cbConfigData };
      cbConfigDataNew.name = "";
      cbConfigDataNew.description = "";
      cbConfigDataNew.motorized = true;
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    }
  };
 
  const gtCbConfigDataHandler = (index) => {
   
    const gtdArray = [...CBGTList];
    if (gtdArray[index]) {
      let cbConfigDataNew = { ...configData.cbConfigData };
      cbConfigDataNew.name = gtdArray[index]?.cbConfigData?.name;
      cbConfigDataNew.description = gtdArray[index]?.cbConfigData?.description;
      cbConfigDataNew.motorized = gtdArray[index]?.cbConfigData?.motorized;

      let derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let cbGtHistory = [];
      if (derStatus[10] === true) {
        if (sessionStorage.getItem("cbGtHistory") === "") {
          cbGtHistory = [];
        } else {
          cbGtHistory = JSON.parse(sessionStorage.getItem("cbGtHistory"));
        }

        if (
          cbGtHistory[index] !== undefined &&
          cbGtHistory[index] !== null
        ) {
          Object.entries(cbGtHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }
     
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
        contactorConfigData: CBGTList?.[index]?.contactorConfigData,
      }));
    } else {
      let cbConfigDataNew = { ...configData?.cbConfigData };
      cbConfigDataNew.name = "";
      cbConfigDataNew.description = "";
      cbConfigDataNew.motorized = true;
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    }
  };

  const evCbConfigDataHandler = (index) => {
    const cbArray = [...CBEVList];
    for(let item of jsonData){

      if(projectTypeValue=="MGaaS IEC" || projectTypeValue === "MGaaS ETO" || projectTypeValue === "NMGaaS ETO"){

      if(item?.componentIdx=="Contactor_60"&&cbArray[index]?.assetType=="EV_charging_station"){
        setIsContactorEnabled(true);
        const configDataNew = {...configData}
        configDataNew.cbConfigData.contactor = true;
        setConfigData((prevState) => ({ ...prevState, configData: configDataNew }))
        }
      }
    }
    if (cbArray[index]) {
      let cbConfigDataNew = { ...configData?.sampleCbConfigData };
      cbConfigDataNew.name = cbArray[index]?.cbConfigData?.name;
      cbConfigDataNew.description = cbArray[index]?.cbConfigData?.description;
      cbConfigDataNew.motorized = cbArray[index]?.cbConfigData?.motorized;
      cbConfigDataNew.contactor = cbArray[index]?.cbConfigData?.contactor;

      let derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let cbEvHistory = [];
      if (derStatus[6]) {
        if (sessionStorage.getItem("cbEvHistory") === "") {
          cbEvHistory = [];
        } else {
          cbEvHistory = JSON.parse(sessionStorage.getItem("cbEvHistory"));
        }

        if (cbEvHistory[index] !== undefined && cbEvHistory[index] !== null) {
          Object.entries(cbEvHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
        contactorConfigData: CBEVList?.[0]?.contactorConfigData,
      }));
    } else {
      let cbConfigDataNew = { ...configData?.sampleCbConfigData };
      cbConfigDataNew.name = "";
      cbConfigDataNew.description = "";
      cbConfigDataNew.motorized = true;
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    }
  };

  const pmConfigDataHandler = (index) => {
    const CBASSETLISTNEW = _.cloneDeep(CBAssetList);
    if (CBASSETLISTNEW[index]) {
      const cbConfigDataNew = { ...configData?.powerMeterData };
      cbConfigDataNew.name = CBASSETLISTNEW[index]?.powerMeterData?.name;
      cbConfigDataNew.description =
        CBASSETLISTNEW[index]?.powerMeterData?.description;

      const derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let pmAssetHistory = [];
      if (derStatus[8]) {
        if (sessionStorage.getItem("pmAssetHistory") === "") {
          pmAssetHistory = [];
        } else {
          pmAssetHistory = JSON.parse(sessionStorage.getItem("pmAssetHistory"));
        }
        if (
          pmAssetHistory[index] !== undefined &&
          pmAssetHistory[index] !== null
        ) {
          Object.entries(pmAssetHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }

      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    } else {
      const cbConfigDataNew = { ...configData?.sampleCbConfigData };
      cbConfigDataNew.name = "";
      cbConfigDataNew.description = "";
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    }
  };

  const existingPmConfigDataHandler = (index) => {
    const CBASSETLISTNEW = _.cloneDeep(CBAssetList);
    if (CBASSETLISTNEW[index]) {
      const cbConfigDataNew = { ...configData?.existingPowerMeterData };
      cbConfigDataNew.name =
        CBASSETLISTNEW[index]?.existingPowerMeterData?.name;
      cbConfigDataNew.description =
        CBASSETLISTNEW[index]?.existingPowerMeterData?.description;

      const derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let pmAssetHistory = [];
      if (derStatus[8]) {
        if (sessionStorage.getItem("pmAssetHistory") === "") {
          pmAssetHistory = [];
        } else {
          pmAssetHistory = JSON.parse(sessionStorage.getItem("pmAssetHistory"));
        }
        if (
          pmAssetHistory[index] !== undefined &&
          pmAssetHistory[index] !== null
        ) {
          Object.entries(pmAssetHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }

      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    } else {
      const cbConfigDataNew = { ...configData?.sampleCbConfigData };
      cbConfigDataNew.name = "";
      cbConfigDataNew.description = "";
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    }
  };

  const loadpmConfigDataHandler = (index) => {
    const CBLoadListNew = _.cloneDeep(CBLoadList);
    if (CBLoadListNew[index]) {
      const cbConfigDataNew = { ...configData?.powerMeterData };
      cbConfigDataNew.name = CBLoadListNew[index]?.powerMeterData?.name;
      cbConfigDataNew.description =
        CBLoadListNew[index]?.powerMeterData?.description;

      const derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let pmEvHistory = [];
      if (derStatus[8]) {
        if (sessionStorage.getItem("pmEvHistory") === "") {
          pmEvHistory = [];
        } else {
          pmEvHistory = JSON.parse(sessionStorage.getItem("pmEvHistory"));
        }
        if (pmEvHistory[index] !== undefined && pmEvHistory[index] !== null) {
          Object.entries(pmEvHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }

      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    } else {
      const cbConfigDataNew = { ...configData?.sampleCbConfigData };
      cbConfigDataNew.name = "";
      cbConfigDataNew.description = "";
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    }
  }

  const evpmConfigDataHandler = (index) => {
    const CBEVListNEW = _.cloneDeep(CBEVList);
    if (CBEVListNEW[index]) {
      const cbConfigDataNew = { ...configData?.powerMeterData };
      cbConfigDataNew.name = CBEVListNEW[index]?.powerMeterData?.name;
      cbConfigDataNew.description =
        CBEVListNEW[index]?.powerMeterData?.description;

      const derStatus = JSON.parse(sessionStorage.getItem("derStatus"));
      let pmEvHistory = [];
      if (derStatus[8]) {
        if (sessionStorage.getItem("pmEvHistory") === "") {
          pmEvHistory = [];
        } else {
          pmEvHistory = JSON.parse(sessionStorage.getItem("pmEvHistory"));
        }
        if (pmEvHistory[index] !== undefined && pmEvHistory[index] !== null) {
          Object.entries(pmEvHistory[index]).forEach(([key, value]) => {
            cbConfigDataNew[key] = value;
          });
        }
      }

      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    } else {
      const cbConfigDataNew = { ...configData?.sampleCbConfigData };
      cbConfigDataNew.name = "";
      cbConfigDataNew.description = "";
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: cbConfigDataNew,
      }));
    }
  };

  //* change handlers

  const handleUtilityChange = (e) => {
    const dataConfig = { ...configData?.configData };
   const onlyNumber = /^(\d+(\.\d+)?|)$/;
    switch (e.target.name) {
      case "utilityCO2":
        if (!isNaN(e.target.value) && e.target.value >= 0) {
          dataConfig[e.target.name] = e.target.value;
          setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
          }));

          assetEditStatusUpdateDER(e, embLabels.UTILITY, true);
        }
        break;
      case "maximum_available_power":
        if (onlyNumber.test(e.target.value)) {
          dataConfig[e.target.name] = e.target.value;
          setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
          }));

          assetEditStatusUpdateDER(e, embLabels.UTILITY, true);
        }
        break;
      case "externalId":
        const { value } = e.target;
        if (value.trim() !== "") {
          if (alphanumericRegWithHyphen.test(value)) {
            setGsxExternalId((prevState) => ({
              ...prevState,
              utilityExternalId: value,
            }));
          }
          assetEditStatusUpdateDER(e, embLabels.UTILITY, true);
        } else {
          setGsxExternalId((prevState) => ({
            ...prevState,
            utilityExternalId: "",
          }));
        }

        break;
      case "co2_emission_rate_type":
        dataConfig[e.target.name] = e.target.value;
        if(e.target.value === "Fixed"){
          dataConfig.co2_emission_rate ="";
            if(dataConfig?.co2Provider) delete dataConfig.co2Provider;
            if(dataConfig?.co2_emission_unit) delete dataConfig.co2_emission_unit;
            setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
          }));
        }else{
           if(dataConfig?.co2_emission_rate)delete dataConfig.co2_emission_rate;
            dataConfig.co2Provider="Watt Time";
            dataConfig.co2_emission_unit="gram(g)";
            
            setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
          }));
        }
         assetEditStatusUpdateDER(e, embLabels.UTILITY, true);
        break;
      case "co2_emission_rate":
            if(onlyNumber.test(e.target.value)){
                dataConfig.co2_emission_rate = e.target.value;
                  setConfigData((prevState) => ({
                  ...prevState,
                  configData: dataConfig,
                }));
                 assetEditStatusUpdateDER(e, embLabels.UTILITY, true);
            }
        
        break;
      default:
        dataConfig[e.target.name] = e.target.value;
        if (/^(?! )[A-Za-z0-9 ]*(?<! )$/.test(e.target.value)) {
          setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
          }));

          assetEditStatusUpdateDER(e, embLabels.UTILITY, true);
        } else {
          setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
          }));
        }
    }
    setIsAssetConfigChanged(configData);
  };

  const handleGensetChange = (e) => {
    const dataConfig = { ...configData?.configData };
   const onlyNumber =/^(\d+(\.\d+)?|)$/;
    if (e.target.name === "gensetCO2") {
      if (!isNaN(e.target.value) && e.target.value >= 0) {
        dataConfig[e.target.name] = e.target.value;
        setConfigData((prevState) => ({
          ...prevState,
          configData: dataConfig,
        }));
        assetEditStatusUpdateDER(e, embLabels.GENSET, true);
      }
    } else if (
      [
        "starting_timer",
        "cooling_timer",
        "minimum_running_period",
        "max_power",
      ].includes(e.target.name)
    ) {
      if (onlyNumber.test(e.target.value)) {
        if (parseInt(e.target.value) === 0) {
          dataConfig[e.target.name] = e.target.value;
        } else {
          dataConfig[e.target.name] = parseInt(e.target.value)
            ? parseInt(e.target.value)
            : "";
        }
        setConfigData((prevState) => ({
          ...prevState,
          configData: dataConfig,
        }));
        assetEditStatusUpdateDER(e, embLabels.GENSET, true);
      }
    } else if (e.target.name === "externalId") {
      const { value } = e.target;
      if (value.trim() !== "") {
        if (alphanumericRegWithHyphen.test(value)) {
          setGsxExternalId((prevState) => ({
            ...prevState,
            gensetExternalId: value,
          }));
        }
        assetEditStatusUpdateDER(e, embLabels.GENSET, true);
      } else {
        setGsxExternalId((prevState) => ({
          ...prevState,
          gensetExternalId: "",
        }));
      }
    } else {
      if( (projectKind?.isDaasAnsi || projectKind?.isDaasEto) && e.target.name === "deviceName" ){
        dataConfig[e.target.name] = e.target.value.toLowerCase();
       }else{
        dataConfig[e.target.name] = e.target.value;
       }

      setConfigData((prevState) => ({
        ...prevState,
        configData: dataConfig,
      }));
      assetEditStatusUpdateDER(e, embLabels.GENSET, true);
    }
    setIsAssetConfigChanged(configData)
  };

  const handleBatteryChange = (e) => {
    const dataConfig = { ...configData?.configData };
    const onlyNumber =/^(\d+(\.\d+)?|)$/;
    if (
      [
        "max_power",
        "capacity",
        "soc_max",
        "soc_min",
        "soc_nominal_power",
       "SoC_min_optimization",
       "SoC_fallback"
      ].includes(e.target.name)
    ) {
      if (onlyNumber.test(e.target.value)) {
        dataConfig[e.target.name] = e.target.value;
        assetEditStatusUpdateDER(e, embLabels.BESS, true);
      } else {
        dataConfig[e.target.name] = e.target.value;
      }
      setConfigData((prevState) => {
        return { ...prevState, configData: dataConfig }
      });
    } else if (e.target.name === "externalId") {
      const { value } = e.target;
      if (value.trim() !== "") {
        if (alphanumericRegWithHyphen.test(value)) {
          setGsxExternalId((prevState) => ({
            ...prevState,
            batteryExternalId: value,
          }));
        }
        assetEditStatusUpdateDER(e, embLabels.BESS, true);
      } else {
        setGsxExternalId((prevState) => ({
          ...prevState,
          batteryExternalId: "",
        }));
      }
    } else if (e.target.name === "deviceName") {
      dataConfig[e.target.name] = e.target.value.toLowerCase();

      setConfigData((prevState) => ({
        ...prevState,
        configData: dataConfig,
      }));

      assetEditStatusUpdateDER(e, embLabels.BESS, true);
    }else if(e.target.name === "quantityOfBess" ){
       if (onlyNumber.test(e.target.value)) {
        dataConfig[e.target.name] = e.target.value;
        dataConfig["max_power"] = Number(e.target.value) * Number(dataConfig.perUnitMaxPower);
         dataConfig["capacity"] = Number(e.target.value) * Number(dataConfig.perUnitMaxEnergy);
        assetEditStatusUpdateDER(e, embLabels.BESS, true);
      } 
      setConfigData((prevState) => ({ ...prevState, configData: dataConfig }));
     
    }else if(e.target.name === "quantityOfBessPro"){
       if (onlyNumber.test(e.target.value)) {
        dataConfig["quantityOfBess"] = e.target.value;
        dataConfig["max_power"] = Number(e.target.value) * 100;
         dataConfig["capacity"] = Number(e.target.value) * 215;
        assetEditStatusUpdateDER(e, embLabels.BESS, true);
      } 
      setConfigData((prevState) => ({ ...prevState, configData: dataConfig }));
    }else if(e.target.name === "perUnitMaxPower"){
       if (onlyNumber.test(e.target.value)) {
        dataConfig[e.target.name] = e.target.value;
        dataConfig["max_power"] = Number(e.target.value) * Number(dataConfig.quantityOfBess ?? 1);
        assetEditStatusUpdateDER(e, embLabels.BESS, true);
      } 
      setConfigData((prevState) => ({ ...prevState, configData: dataConfig }));
    }else if(e.target.name ===  "perUnitMaxEnergy"){
       if (onlyNumber.test(e.target.value)) {
        dataConfig[e.target.name] = e.target.value;
         dataConfig["capacity"] = Number(e.target.value) * Number(dataConfig.quantityOfBess ?? 1);
        assetEditStatusUpdateDER(e, embLabels.BESS, true);
      } 
      setConfigData((prevState) => ({ ...prevState, configData: dataConfig }));
    }
     else {
      dataConfig[e.target.name] = e.target.value;

      setConfigData((prevState) => ({
        ...prevState,
        configData: dataConfig,
      }));

      assetEditStatusUpdateDER(e, embLabels.BESS, true);
    }
    setIsAssetConfigChanged(configData);
  };

  const handleChpChange = (e) => {
    let dataConfig = { ...configData?.configData };
    const onlyNumber =/^(\d+(\.\d+)?|)$/;
    switch (e.target.name) {
      case "nominal_power":
        if (onlyNumber.test(e.target.value)) {
          dataConfig[e.target.name] = e.target.value;

          assetEditStatusUpdateDER(e, embLabels.CHP, true);
        } else {
          dataConfig[e.target.name] = configData?.configData?.nominal_power;
        }
        setConfigData((prevState) => ({
          ...prevState,
          configData: dataConfig,
        }));
        break;

      default:
        dataConfig[e.target.name] = e.target.value;
        // if (isValidName(e.target.value)) {
          setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
          }));
          assetEditStatusUpdateDER(e, embLabels.CHP, true);
        // }
    }
  };

  const handleHybridChange = (e) => {
    const dataConfig = { ...configData?.configData };
    const onlyNumber =/^(\d+(\.\d+)?|)$/;
    const { name, value } = e.target;
    if (
      [
        "max_capacity",
        "capacity",
        "max_solar_power",
        "max_inverter_output_power",
        "usableStorageCapacityHr",
        "usableStorageCapacity",
      ].includes(name)
    ) {
      if (onlyNumber.test(value)) {
        dataConfig[name] = value;
        assetEditStatusUpdateDER(e, embLabels.HYBRID, true);
      } else {
        dataConfig[e.target.name] = e.target.name;
      }
      setConfigData((prevState) => ({ ...prevState, configData: dataConfig }));
    } else {
      dataConfig[name] = value;
      // if (isValidName(value)) {
        setConfigData((prevState) => ({
          ...prevState,
          configData: dataConfig,
        }));
      // }
      assetEditStatusUpdateDER(e, embLabels.HYBRID, true);
    }
    setIsAssetConfigChanged(configData);
  };

  const handlePvChange = (e) => {
    // if (isValidName(e.target.value)) {
      PVDetailsDispatch({
        type: ACTIONS.HANDLE_PV_CHANGE,
        payload: {
          data: e.target,
          projectTypeValue
        },
      });
      setConsiderSelected(false);
    // }
  };

  const handleLoadChange = (e) => {
    const {name, value} = e.target;
    let newConfigData = { ...configData?.configData };
    const onlyNumber =  /^(\d+(\.\d+)?|)$/;
    switch (name) {
      case "maximum_available_power":
        if (onlyNumber.test(value)) {
          newConfigData[name] = value;
          assetEditStatusUpdateLoad(e, true, selectedValues?.selectedLoad);
        } else {
          newConfigData[name] =
            configData?.configData?.maximum_available_power;
        }
        setConfigData((prevState) => ({
          ...prevState,
          configData: newConfigData,
        }));

        break;
      case "name":
        // if(isValidName(value) || value === ""){
          let loadNamesList = [];
        CBLoadList?.forEach((load) => {
          loadNamesList.push(load?.configData?.name);
        });
        newConfigData[name] = value;
        if (loadNamesList.includes(value)) {
          setIsLoadNameUnique(true);
        } else {
          setIsLoadNameUnique(false);
        }
        setConfigData((prevState) => ({
          ...prevState,
          configData: newConfigData,
        }));
        assetEditStatusUpdateLoad(e, true, selectedValues?.selectedLoad);
        // }else{
        //    setConfigData((prevState) => ({
        //     ...prevState,
        //     configData: newConfigData,
        //   }));
        // }
        
        break;
      default:
        newConfigData[e.target.name] = e.target.value;
        if (isValidName(e.target.value)) {
          setConfigData((prevState) => ({
            ...prevState,
            configData: newConfigData,
          }));
          assetEditStatusUpdateLoad(e, true, selectedValues?.selectedLoad);
        } else {
          setConfigData((prevState) => ({
            ...prevState,
            configData: newConfigData,
          }));
          // callSnackbar("error", "only alphanumeric charecters are allowed")
        }
    }
     setIsAssetConfigChanged(configData);
  };
const handleGtChange = (e) => {
    let dataConfig = { ...configData.gtConfigData };
    const onlyNumber =  /^(\d+(\.\d+)?|)$/;
    switch (e.target.name) {
         case "name":
           dataConfig[e.target.name] = e.target.value;
        setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
            gtConfigData: dataConfig,
          }));
     assetEditStatusUpdateDER(e, embLabels.GT, true);
        break;
      default:
        dataConfig[e.target.name] = e.target.value;
       
          setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
            gtConfigData: dataConfig,
          }));
      assetEditStatusUpdateDER(e, embLabels.GT, true);
       
    }
    setIsAssetConfigChanged(configData);
}
  const handleEvChange = (e) => {
    let dataConfig = { ...configData?.configData };
    const onlyNumber =  /^(\d+(\.\d+)?|)$/;
    switch (e.target.name) {
      case "maximum_available_power":
        if (onlyNumber.test(e.target.value)) {
          dataConfig[e.target.name] = e.target.value;
          assetEditStatusUpdateDER(e, embLabels.EV, true);
        } else {
          dataConfig[e.target.name] =
            configData?.configData?.maximum_available_power;
        }
        setConfigData((prevState) => ({
          ...prevState,
          configData: dataConfig,
          evConfigData: dataConfig,
        }
        
      ));
        break;
         case "name":
        if(isEvce){ 
          //  const regex = /^[A-Za-z]+$/.test(e.target.value);
          //  if(!regex && e.target.value !== "") return;
           dataConfig[e.target.name] = e.target.value;
        }else{
           dataConfig[e.target.name] = e.target.value;
        }
        setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
            evConfigData: dataConfig,
          }));
          assetEditStatusUpdateDER(e, embLabels.EV, true);
        break;
      default:
        dataConfig[e.target.name] = e.target.value;
        // if (isValidName(e.target.value)) {
          setConfigData((prevState) => ({
            ...prevState,
            configData: dataConfig,
            evConfigData: dataConfig,
          }));
          assetEditStatusUpdateDER(e, embLabels.EV, true);
        // }
    }
    setIsAssetConfigChanged(configData);
  };

  const handleCBConfigChange = (e) => {
    const newCbConfigData = { ...configData?.cbConfigData };
    if (e.target.name === "motorized") {
      if (
        [embLabels.GRID_CONNECTED,embLabels.ISLANDABLE, embLabels.OFF_GRID].includes(siteType) &&
        clientType === embLabels.GENERIC_PROJECT &&
       ( projectTypeValue === "MGaaS IEC" || projectTypeValue === "MGaaS ETO"|| projectTypeValue === "NMGaaS ETO")
      ) {
        if(e.target.checked){
          CBAssetListDispatch({
            type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG,
            payload: {
              selectedAssetCB: selectedValues?.selectedAssetCB,
              cbConfigData: configData?.contactorConfigData,
              checked: false,
            },
          })
            newCbConfigData.motorized = true;
            setConfigData((prevState)=>({
              ...prevState,
              cbConfigData: newCbConfigData,
            }))
        }else{
          newCbConfigData[e.target.name] = e.target.checked;
        }
      } else {
        newCbConfigData[e.target.name] = true;
      }
    }else if(e.target.name==="contactor"){
      if (
        [embLabels.GRID_CONNECTED,embLabels.ISLANDABLE, embLabels.OFF_GRID].includes(siteType)  &&
        clientType === embLabels.GENERIC_PROJECT &&
        (projectTypeValue === "MGaaS IEC" || projectTypeValue === "MGaaS ETO"|| projectTypeValue === "NMGaaS ETO")
      ) {
         if(e.target.checked){
          if(configData?.cbConfigData?.motorized){
            const newConfigData = {...configData}
            newConfigData.cbConfigData.motorized = false
            newConfigData.cbConfigData[e.target.name] = e.target.value;
            setConfigData((prevState)=>({
              ...prevState,
              cbConfigData: newConfigData,
            }))
          }
        }
      } else {
        newCbConfigData[e.target.name] = true;
      }
    } else {
      if( (projectKind?.isDaasAnsi || projectKind?.isDaasEto) && e.target.name === "name" ){
        newCbConfigData[e.target.name] = e.target.value.toLowerCase();
      }else{
        newCbConfigData[e.target.name] = e.target.value;
      }
    }
    // if (isValidName(e.target.value)) {
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: newCbConfigData,
      }));
    // }
    setIsAssetConfigChanged(configData);
  };

  const handleCBContactorConfigChange = (e) => {
    const newCbContactorConfigData = { ...configData?.contactorConfigData };
    newCbContactorConfigData[e.target.name] = e.target.value;

    // if (isValidName(e.target.value)) {
      setConfigData((prevState) => {
        CBAssetListDispatch({
          type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG,
          payload: {
            selectedAssetCB: selectedValues?.selectedAssetCB,
            cbConfigData: newCbContactorConfigData,
            checked: CBAssetList?.[selectedValues?.selectedAssetCB]?.uiData?.contactor
          },
        });
       return { ...prevState,
        contactorConfigData: newCbContactorConfigData}
      });
    // }

    setIsAssetConfigChanged(newCbContactorConfigData)
  };
  const handleCBContactorConfigChangeLoad = (e) => {
    const newCbContactorConfigData = { ...configData?.contactorConfigData };
    newCbContactorConfigData[e.target.name] = e.target.value;

    // if (isValidName(e.target.value)) {
      setConfigData((prevState) => {
         CBLoadListDispatch({
               type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_CHANGE,
              payload: {
                index: selectedValues?.selectedLoadCB,
                CBContactor: newCbContactorConfigData,
                },
            })
        return { ...prevState,
        contactorConfigData: newCbContactorConfigData}
      });
    // }

    setIsAssetConfigChanged(newCbContactorConfigData)
  };
  const handleCBConfigChangeLoad = (e) => {
    const newCbConfigData = { ...configData?.cbConfigData };
    let newContactorConfigData = { ...configData?.contactorConfigData };
    
    if (e.target.name === "motorized") {
      if(e.target.checked){
       CBLoadListDispatch({
         type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
         payload: {
           index: selectedValues?.selectedLoadCB,
           CBContactor: configData?.contactorConfigData,
           defaultContactor: configData?.contactorConfigData,
           checked: false,
         },
       })
         newCbConfigData[e.target.name] = e.target.checked;
         newCbConfigData.loadCategory = 0;
         newCbConfigData.loadCategory = 0;
         setConfigData((prevState)=>({
           ...prevState,
           cbConfigData: newCbConfigData,
         }))
     }else{
       newCbConfigData[e.target.name] = e.target.checked;
     }
     newCbConfigData[e.target.name] = e.target.checked;
     newCbConfigData.loadCategory = 0;
     newCbConfigData.loadCategory = 0;
   }else if(e.target.name==='contactor'){
     // Generate the same name as the reducer
       const index = selectedValues.selectedLoadCB;
       newContactorConfigData = { ...configData.contactorConfigData };
       newContactorConfigData.name = `Load_Contactor_${index + 1}`;
     if(e.target.checked){
       // Generate the same name as the reducer
       const index = selectedValues?.selectedLoadCB;
       newContactorConfigData = { ...configData?.contactorConfigData };
       newContactorConfigData.name = `Load_Contactor_${index + 1}`;
       
       if(newCbConfigData.motorized){
        CBLoadListDispatch({
           type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
           payload: {
             index: selectedValues?.selectedLoadCB,
             CBContactor: newContactorConfigData,
             defaultContactor: defaultPreFillData[3],
             checked: true,
           },
         })
         newCbConfigData.motorized = false;
         }else{
          CBLoadListDispatch({
             type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
             payload: {
               index: selectedValues?.selectedLoadCB,
               CBContactor: newContactorConfigData,
               defaultContactor: defaultPreFillData[3],
               checked: true,
             },
           })
       }
     }else{
       newContactorConfigData = { name: "", description: "" };
       
      CBLoadListDispatch({
         type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
         payload: {
           index: selectedValues?.selectedLoadCB,
           CBContactor: newContactorConfigData,
            defaultContactor: defaultPreFillData[3],
           checked: false,
         },
       })
     }
   } else {
    if( (projectKind?.isDaasAnsi || projectKind?.isDaasEto) && e.target.name === "name" ){
      newCbConfigData[e.target.name] = e.target.value.toLowerCase();
    }else{
      newCbConfigData[e.target.name] = e.target.value;
    }
     
   }

    // if (isValidCBName(e.target.value)) {
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: newCbConfigData,
        contactorConfigData: newContactorConfigData,
      }));
    // }
    setIsAssetConfigChanged(configData)
   setTimeout(() => {
     const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
    setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
   }, 500);
  };
  const handleGtCbConfigChange = (e) => {
      const newCbConfigData = { ...configData.cbConfigData };
    let newContactorConfigData = { ...configData.contactorConfigData };
    if (e.target.name === "motorized") {
      if(e.target.checked){
       CBGTListDispatch({
         type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
         payload: {
           index: selectedValues.selectedGtCB,
           CBContactor: configData.contactorConfigData,
          defaultContactor: configData.contactorConfigData,
           checked: false,
         },
       })
         newCbConfigData[e.target.name] = e.target.checked;
         newCbConfigData.loadCategory = 0;
         newCbConfigData.loadCategory = 0;
         setConfigData((prevState)=>({
           ...prevState,
           cbConfigData: newCbConfigData,
         }))
     }else{
       newCbConfigData[e.target.name] = e.target.checked;
     }
     newCbConfigData[e.target.name] = e.target.checked;
     newCbConfigData.loadCategory = 0;
     newCbConfigData.loadCategory = 0;
   }else if(e.target.name==='contactor'){
     // Generate the same name as the reducer
       const index = selectedValues.selectedGtCB;
       newContactorConfigData = { ...configData.contactorConfigData };
       newContactorConfigData.name = `Load_Contactor_${index + 1}`;
     if(e.target.checked){
       if(newCbConfigData.motorized){
        CBGTListDispatch({
           type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
           payload: {
             index: selectedValues.selectedGtCB,
             CBContactor: newContactorConfigData,
             defaultContactor: defaultPreFillData[3],
             checked: true,
           },
         })
         newCbConfigData.motorized = false;
         }else{
          CBGTListDispatch({
             type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
             payload: {
               index: selectedValues.selectedLoadCB,
              CBContactor: newContactorConfigData,
               defaultContactor: defaultPreFillData[3],
               checked: true,
             },
           })
       }
     }else{
      newContactorConfigData = { name: "", description: "" };
      CBGTListDispatch({
         type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
         payload: {
           index: selectedValues.selectedLoadCB,
           CBContactor: newContactorConfigData,
            defaultContactor: defaultPreFillData[3],
           checked: false,
         },
       })
     }
   } else {
      newCbConfigData[e.target.name] = e.target.value;
      }

    // if (isValidCBName(e.target.value)) {
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: newCbConfigData,
        contactorConfigData: newContactorConfigData,
      }));
    // }
    setIsAssetConfigChanged(configData)
   setTimeout(() => {
     const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
    setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
   }, 500);
  }

  const handleCBConfigChangeEv = (e) => {
    const newCbConfigData = { ...configData?.cbConfigData };
    if (e.target.name === "motorized") {
       if(e.target.checked){
        CBEVListDispatch({
          type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
          payload: {
            index: selectedValues?.selectedEvCB,
            CBContactor: configData?.contactorConfigData,
            defaultContactor: configData?.contactorConfigData,
            checked: false,
          },
        })
          newCbConfigData.motorized = true;
          setConfigData((prevState)=>({
            ...prevState,
            cbConfigData: newCbConfigData,
          }))
      }else{
        newCbConfigData[e.target.name] = e.target.checked;
      }
      newCbConfigData[e.target.name] = e.target.checked;
      newCbConfigData.loadCategory = 0;
      newCbConfigData.loadCategory = 0;
    }else if(e.target.name==='contactor'){
      if(e.target.checked){
        if(newCbConfigData.motorized){
          CBEVListDispatch({
            type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
            payload: {
              index: selectedValues?.selectedEvCB,
              CBContactor: configData?.contactorConfigData,
              defaultContactor: configData?.contactorConfigData,
              checked: true,
            },
          })
          newCbConfigData.motorized = false;
          }else{
            CBEVListDispatch({
              type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
              payload: {
                index: selectedValues?.selectedEvCB,
                CBContactor: configData?.contactorConfigData,
                defaultContactor: configData?.contactorConfigData,
                checked: true,
              },
            })
        }
      }else{
        CBEVListDispatch({
          type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
          payload: {
            index: selectedValues?.selectedEvCB,
            CBContactor: configData?.contactorConfigData,
            defaultContactor: configData?.contactorConfigData,
            checked: false,
          },
        })
      }
    } else {
      newCbConfigData[e.target.name] = e.target.value;
    }

    // if (isValidName(e.target.value)) {
      setConfigData((prevState) => ({
        ...prevState,
        cbConfigData: newCbConfigData,
      }));
    // }
    setIsAssetConfigChanged(configData);
  };

  //* Update config data's
  //* isBetween is now imported from designAssetUtils.js

  const calculateMaxAvailableUtilityPower = () => {
    const nominalCurrentMin =
      CBAssetList[0]?.cbConfigData?.cbAssetDetails[0]?.assetAttributes?.filter(
        (assetAttribute) => assetAttribute.type === "nominalCurrentMin"
      )[0]?.value;
    const nominalCurrentMax =
      CBAssetList[0]?.cbConfigData?.cbAssetDetails[0]?.assetAttributes?.filter(
        (assetAttribute) => assetAttribute.type === "nominalCurrentMax"
      )[0]?.value;
    const nominalPowerMin = nominalCurrentMin
      ? nominalCurrentMin * nominalVoltage
      : +defaultsMinMax?.maxAvailablePowerUtilityMin;
    const nominalPowerMax = nominalCurrentMax
      ? nominalCurrentMax * nominalVoltage
      : +defaultsMinMax?.maxAvailablePowerUtilityMax;

    const powerIsBetween = isBetween(
      +configData?.configData?.maximum_available_power,
      nominalPowerMin,
      nominalPowerMax
    );

    return [powerIsBetween, nominalPowerMin, nominalPowerMax];
  };
  const calculateMaxAvailableLoadPower = () => {
    const nominalCurrentMin = CBLoadList?.[
      selectedValues?.selectedLoad
    ]?.cbConfigData?.cbAssetDetails?.[0]?.assetAttributes?.filter(
      (assetAttribute) => assetAttribute.type === "nominalCurrentMin"
    )[0]?.value;
    const nominalCurrentMax = CBLoadList?.[
      selectedValues?.selectedLoad
    ]?.cbConfigData?.cbAssetDetails?.[0]?.assetAttributes?.filter(
      (assetAttribute) => assetAttribute.type === "nominalCurrentMax"
    )[0]?.value;

    let nominalPowerMin = nominalCurrentMin
      ? nominalCurrentMin * nominalVoltage
      : +defaultsMinMax?.maxPowerLoadMin;
    let nominalPowerMax = nominalCurrentMax
      ? nominalCurrentMax * nominalVoltage
      : +defaultsMinMax?.maxPowerLoadMax;

    if (siteType === embLabels.GENSET_PVBESS_A2E) {
      nominalPowerMin =
        +CBAssetList[7].assetDetails[0].assetAttributes.find(
          (attribute) => attribute.type === "minLoadPower"
        ).value ?? "0";
      nominalPowerMax =
        +CBAssetList[7].assetDetails[0].assetAttributes.find(
          (attribute) => attribute.type === "maxLoadPower"
        ).value ?? "200";
    }

    const powerIsBetween = isBetween(
      +configData?.configData?.maximum_available_power,
      nominalPowerMin,
      nominalPowerMax
    );

    return [powerIsBetween, nominalPowerMin, nominalPowerMax];
  };

  const calculateMaxAvailableGensetPower = () => {
    const nominalPowerMin = Number(
      CBAssetList[7]?.assetDetails[0]?.assetAttributes?.find(
        (attribute) => attribute.type === "minGensetPower"
      )?.value ?? defaultsMinMax?.maxPowerGensetMin
    );

    const nominalPowerMax = Number(
      CBAssetList[7].assetDetails[0]?.assetAttributes?.find(
        (attribute) => attribute.type === "maxGensetPower"
      )?.value ?? defaultsMinMax?.maxPowerGensetMax
    );

    const powerIsBetween = isBetween(
      +configData?.configData?.max_power,
      +nominalPowerMin,
      +nominalPowerMax
    );

    return [powerIsBetween, nominalPowerMin, nominalPowerMax];
  };
 
  const updateUtilitytConfig = () => {
    const [powerIsBetween, nominalPowerMin, nominalPowerMax] =
      calculateMaxAvailableUtilityPower();
    if (
      checkForDuplicateName(
        configData?.configData?.name,
        CBAssetList[0]?.configData?.name
      )
    ) {
      callSnackbar(
        `error`,
        `"${configData?.configData?.name}" already exists, please provide an unique name`
      );
    } else {
      if(projectTypeValue === "NMGaaS ETO"&& configData?.configData?.co2_emission_rate_type ==="Fixed" && configData?.configData?.co2_emission_rate === ""){
         callSnackbar("error", embLabels.mandatoryFields);
      }else if (
        /^[a-zA-Z0-9-_& ]+$/.test(configData?.configData?.name) &&
        parseInt(configData?.configData?.maximum_available_power) !== 0 &&
         (configData?.configData?.maximum_available_power !== "")  &&
       (projectTypeValue === "NMGaaS ETO" || ( parseInt(configData?.configData?.utilityCO2) >=
          defaultsMinMax?.utilityCO2Min &&
        parseInt(configData?.configData?.utilityCO2) <=
          defaultsMinMax?.utilityCO2Max &&
        powerIsBetween ))
      ) {
        CBAssetListDispatch({
          type: ACTIONS.UPDATE_ASSET_CONFIG,
          payload: {
            selectedAsset: selectedValues?.selectedAsset,
            configData: configData?.configData,
          },
        });
        if (duplicateUpdate.der === "") {
          setDuplicateUpdate({
            der: 0,
            load: "",
            pv: "",
            ev: "",
          });
        }
        updateDerHistory(0);
        updateDerStatus(0);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "Utility Configured Successfully");
      } else if (
        configData?.configData?.name === "" ||
        configData?.configData?.maximum_available_power?.toString() === "" ||
        configData?.configData?.utilityCO2.toString() === ""
      ) {
        callSnackbar("error", embLabels.mandatoryFields);
      // } else if (!isValidName(configData?.configData?.name)) {
      //   callSnackbar("error", embLabels.notallowedAlphanumericValues);
      } else if (
        parseInt(configData?.configData?.utilityCO2) <
          defaultsMinMax?.utilityCO2Min ||
        parseInt(configData?.configData?.utilityCO2) >
          defaultsMinMax?.utilityCO2Max
      ) {
        callSnackbar(
          `error`,
          `Utility CO2 Emission should be in range(${defaultsMinMax?.utilityCO2Min}-${defaultsMinMax?.utilityCO2Max} g/kWh)`
        );
      } else if (!powerIsBetween) {
        callSnackbar(
          `error`,
          `Maximum Available Power should be in range
          (${nominalPowerMin}-${nominalPowerMax} kW)`
        );
      } else {
        callSnackbar("error", embLabels.mandatoryFields);
      }
    }
  };

  const updateGensetConfig = () => {
    const deviceNameCheck = typeof configData?.configData?.deviceName === 'string' && /^[a-zA-Z0-9-_]+$/.test(configData?.configData?.deviceName);
    const [powerIsBetween, nominalPowerMin, nominalPowerMax] =
      calculateMaxAvailableGensetPower();
    if (
      checkForDuplicateName(
        configData?.configData?.name,
        CBAssetList[1]?.configData?.name
      )
    ) {
      callSnackbar(
        "error",
        `"${configData?.configData?.name}" already exists, please provide an unique name`
      );
    } else {
      if (parseInt(configData?.configData?.starting_timer) !== 0 && 
      parseInt(configData?.configData?.cooling_timer) !== 0 && 
      parseInt(configData?.configData?.minimum_running_period) !== 0 &&
        /^[a-zA-Z0-9-_& ]+$/.test(configData?.configData?.name ) &&
        // isValidName(configData.configData.name) &&
        parseInt(configData?.configData?.gensetCO2) >=
          defaultsMinMax?.gensetCO2Min &&
        parseInt(configData?.configData?.gensetCO2) <=
          defaultsMinMax?.gensetCO2Max &&
        parseInt(configData?.configData?.starting_timer) >=
          defaultsMinMax?.startUpGensetMin &&
        parseInt(configData?.configData?.starting_timer) <=
          defaultsMinMax?.startUpGensetMax &&
        parseInt(configData?.configData?.cooling_timer) >=
          defaultsMinMax?.coolingDurationGensetMin &&
        parseInt(configData?.configData?.cooling_timer) <=
          defaultsMinMax?.coolingDurationGensetMax &&
        parseInt(configData?.configData?.minimum_running_period) >=
          defaultsMinMax?.minRunningDurationGensetMin &&
        parseInt(configData?.configData?.minimum_running_period) <=
          defaultsMinMax?.minRunningDurationGensetMax &&
        powerIsBetween
      ) {
        if(!deviceNameCheck &&( projectTypeValue === "DaaS ANSI" || projectTypeValue === "DaaS ETO")){
          callSnackbar(`error`, embLabels.mandatoryFields);
        }else{
          CBAssetListDispatch({
            type: ACTIONS.UPDATE_ASSET_CONFIG,
            payload: {
              selectedAsset: selectedValues?.selectedAsset,
              configData: configData?.configData,
            },
          });
          if (duplicateUpdate.der === "") {
            setDuplicateUpdate({
              der: 1,
              load: "",
              pv: "",
              ev: "",
            });
          }
  
          // newGensetData[this.state.selectedAsset].communicationData =
          //   Object.assign({}, this.state.communicationData);
  
          updateDerHistory(1);
          updateDerStatus(1);
  
          const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
          setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
          sessionStorage.setItem("componentDataModified",true);
          callSnackbar("information", "Genset Configured Successfully");
        }
      } else if (
        configData?.configData?.name.toString() === "" ||
        configData?.configData?.max_power?.toString() === "" ||
        configData?.configData?.gensetCO2?.toString() === "" ||
        configData?.configData?.starting_timer?.toString() === "" ||
        configData?.configData?.cooling_timer?.toString() === "" ||
        configData?.configData?.minimum_running_period.toString() === ""
      ) {
        callSnackbar("error", embLabels.mandatoryFields);
      // } else if (!isValidName(configData?.configData?.name)) {
      //   callSnackbar("error", embLabels.notallowedAlphanumericValues);
      } else if (!powerIsBetween) {
        callSnackbar(
          `error`,
          `Max Power should be in range
          (${nominalPowerMin}-${nominalPowerMax} kW)`
        );
      } else if (
        parseInt(configData?.configData?.gensetCO2) <
          defaultsMinMax?.gensetCO2Min ||
        parseInt(configData?.configData?.gensetCO2) > defaultsMinMax?.gensetCO2Max
      ) {
        callSnackbar(
          `error`,
          `Genset CO2 should be in range (${defaultsMinMax?.gensetCO2Min}-${defaultsMinMax?.gensetCO2Max} kg/MWh)`
        );
      } else if (
        parseInt(configData?.configData?.starting_timer) <
          defaultsMinMax?.startUpGensetMin ||
        parseInt(configData?.configData?.starting_timer) >
          defaultsMinMax?.startUpGensetMax
      ) {
        callSnackbar(
          `error`,
          `Start-up duration should be in range (${defaultsMinMax?.startUpGensetMin}-${defaultsMinMax?.startUpGensetMax} s)`
        );
      } else if (
        parseInt(configData?.configData?.cooling_timer) <
          defaultsMinMax?.coolingDurationGensetMin ||
        parseInt(configData?.configData?.cooling_timer) >
          defaultsMinMax?.coolingDurationGensetMax
      ) {
        callSnackbar(
          `error`,
          `Cooling Duration should be in range (${defaultsMinMax?.coolingDurationGensetMin}-${defaultsMinMax?.coolingDurationGensetMax} s)`
        );
      } else if (
        parseInt(configData?.configData?.minimum_running_period) <
          defaultsMinMax?.minRunningDurationGensetMin ||
        parseInt(configData?.configData?.minimum_running_period) >
          defaultsMinMax?.minRunningDurationGensetMax
      ) {
        callSnackbar(
          `error`,
          `Minimum running duration should be in range (${defaultsMinMax?.minRunningDurationGensetMin}-${defaultsMinMax?.minRunningDurationGensetMax} s)`
        );
      } else {
        callSnackbar("error", embLabels.mandatoryFields);
      }
    }
  };

  const updateBatteryConfig = (index) => {
    const isEnergyReserveSelected = CBAssetList?.[index]?.uiData?.energyReserveRequired;
    const maxPowerBessMax = configData?.configData?.quantityOfBess?(configData?.configData?.quantityOfBess  * defaultsMinMax?.maxPowerBessMax):defaultsMinMax?.maxPowerBessMax;
  const capacityBessMax = configData?.configData?.quantityOfBess?(configData?.configData?.quantityOfBess  * defaultsMinMax?.capacityBessMax):defaultsMinMax?.capacityBessMax;
 
    const deviceNameCheck = typeof configData?.configData?.deviceName === 'string' && (/^[a-zA-Z0-9-_]+$/.test(configData?.configData?.deviceName));
    if(configData?.configData?.max_power!=undefined&&configData?.configData?.max_power!=""){
    let values = JSON.parse(configData?.configData?.max_power)
      if(Array.isArray(values)){
        const configDataNew = {...configData?.configData}
        configDataNew.max_power = values[0];
        setConfigData((prevState)=>({...prevState,configData:configDataNew}))
      }
    }
    if (
      checkForDuplicateName(
        configData?.configData?.name,
        CBAssetList?.[2]?.configData?.name
      )
    ) {
      callSnackbar(
        `error`,
        `"${configData?.configData?.name}" already exists, please provide an unique name"`
      );
    } else {
      if (
        /^[a-zA-Z0-9-_& ]+$/.test(configData?.configData?.name)  &&
       
        // isValidName(configData?.configData?.name) &&
        ( 
          (parseInt(configData?.configData?.max_power) >=
            defaultsMinMax?.maxPowerBessMin &&
            parseInt(configData?.configData?.max_power) <=
              maxPowerBessMax)) &&
        parseInt(configData?.configData?.capacity) >=
          defaultsMinMax?.capacityBessMin &&
        parseInt(configData?.configData?.capacity) <=
          capacityBessMax &&
        parseInt(configData?.configData?.soc_max) >= parseInt(defaultsMinMax?.socMaxMin) &&
        parseInt(configData?.configData?.soc_max) <= parseInt( defaultsMinMax?.socMaxMax) &&
        parseInt(configData?.configData?.soc_min) >= parseInt(defaultsMinMax?.socMinMin )&&
        parseInt(configData?.configData?.soc_min) <= parseInt(defaultsMinMax?.socMinMax) &&
        (parseInt(configData.configData.SoC_min_optimization) > parseInt(configData?.configData?.soc_min)) &&
         (parseInt(configData.configData.SoC_min_optimization) < parseInt(defaultsMinMax.socMinMax) )&&
         (  parseInt(configData.configData.SoC_min_optimization) < parseInt(defaultsMinMax?.socEmergencyDefault)) &&
         (parseInt(configData.configData.SoC_fallback) > parseInt(configData.configData.soc_min) 
         && parseInt(configData.configData.SoC_fallback) < parseInt(configData.configData.soc_max)) 
        // (((siteType === embLabels.ISLANDABLE|| siteType === embLabels.OFF_GRID || siteType === embLabels.ISLANDABLE_GSX)
        //  && (parseInt(configData?.configData?.soc_nominal_power) > parseInt(defaultsMinMax?.socEmergencyDefault)) 
        //  && (parseInt(configData?.configData?.soc_nominal_power) > parseInt(defaultsMinMax?.socEssentialDefault)) 
        //  && (parseInt(configData?.configData?.soc_nominal_power) < parseInt(configData?.configData?.soc_max)))
        //   || ((siteType === embLabels.GRID_CONNECTED || siteType === embLabels.GRID_CONNECTED_GSX) &&
        //    ((parseInt(configData?.configData?.soc_nominal_power) > parseInt(configData?.configData?.soc_min))
        //     &&  (parseInt(configData?.configData?.soc_nominal_power) < parseInt(configData?.configData?.soc_max))) )) 

        // && (parseInt(configData?.configData?.soc_nominal_power) > 0 && parseInt(configData.configData.soc_nominal_power) <= 100) 
        ) {
        if( !deviceNameCheck &&( projectTypeValue === "DaaS ANSI" || projectTypeValue === "DaaS ETO")){
          callSnackbar(`error`, embLabels.mandatoryFields);
        }else{
          const configDataNew = {...configData?.configData}
          configDataNew.energyReserveRequired = isEnergyReserveSelected;
          CBAssetListDispatch({
            type: ACTIONS.UPDATE_ASSET_CONFIG,
            payload: {
              selectedAsset: selectedValues?.selectedAsset,
              configData: configDataNew,
              isBessController: isBessController
            },
          });
  
          // newBatteryData[this.state.selectedAsset].communicationData =
          // Object.assign({}, this.state.batteryCommunicationData);
          if (duplicateUpdate.der === "") {
            setDuplicateUpdate({
              der: 2,
              load: "",
              pv: "",
              ev: "",
            });
          }
  
          updateDerHistory(2);
          updateDerStatus(2);
  
          const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
          setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
          sessionStorage.setItem("componentDataModified",true);
          callSnackbar("information", "Battery Configured Successfully");
        }
       
      }else if(((siteType !== embLabels.ISLANDABLE ||  siteType !== embLabels.OFF_GRID || siteType !== embLabels.ISLANDABLE_GSX) && !((parseInt(configData?.configData?.soc_nominal_power) > parseInt(defaultsMinMax?.socEmergencyDefault)) && (parseInt(configData?.configData?.soc_nominal_power) > parseInt(defaultsMinMax?.socEssentialDefault))))){
        callSnackbar(
          'error',
          `SoC Nominal should be greater than SoC B (${defaultsMinMax?.socEssentialDefault}) and SoC C (${defaultsMinMax?.socEmergencyDefault}), and less than ${configData?.configData?.soc_max} of Max power.`
        );
      }else if(((siteType !== embLabels.GRID_CONNECTED || siteType !== embLabels.GRID_CONNECTED_GSX) && !((parseInt(configData?.configData?.soc_nominal_power) > parseInt(configData?.configData?.soc_min)) && (parseInt(configData?.configData?.soc_nominal_power) < parseInt(configData?.configData?.soc_max))))){
        callSnackbar(
          'error',
          `SoC Nominal should be greater than SoC B (${defaultsMinMax?.socEssentialDefault}) and SoC C (${defaultsMinMax?.socEmergencyDefault}), and less than ${configData?.configData?.soc_max} of Max power.`
        );
      }else if(Number(configData?.configData?.SoC_fallback )< Number( configData?.configData?.soc_min) || Number(configData?.configData?.SoC_fallback) > Number(configData?.configData?.soc_max)){
        callSnackbar(
          'error',
          `SoC fallback should be greater than SoC Min Manufacturer(
                    ${configData?.configData?.soc_min}) and less than
                    ${configData?.configData?.soc_max} of SoC Max Manufacturer.`
        );
      }else if( parseInt(configData?.configData?.SoC_min_optimization) <=
              configData?.configData?.soc_min ||
              parseInt(configData?.configData?.SoC_min_optimization) >
                defaultsMinMax?.socMinMax ||
                parseInt(configData?.configData?.SoC_min_optimization) >
               defaultsMinMax?.socEmergencyDefault){
              callSnackbar(`error`,` SoC Min optimization should be greater than SoC Min Manufacturer(
                    ${configData?.configData?.soc_min}) and less than SoC Emergency (
                    ${defaultsMinMax?.socEmergencyDefault})`)
      }
      else if((parseInt(configData?.configData?.soc_nominal_power) < 1 || parseInt(configData?.configData?.soc_nominal_power) > 100) ){
        callSnackbar(`error`, embLabels.SOC_NOMINAL_RANGE);
      }
 else if (
        [
          configData?.configData?.name,
          configData?.configData?.max_power?.toString(),
          configData?.configData?.capacity?.toString(),
          configData?.configData?.soc_max?.toString(),
          configData?.configData?.soc_min?.toString(),
          configData?.configData?.soc_nominal_power?.toString(),
          
        ].includes("")
      ) {
        callSnackbar(`error`, embLabels.mandatoryFields);
      // } else if (!isValidName(configData?.configData?.name)) {
      //   callSnackbar("error", embLabels.notallowedAlphanumericValues);
      } else if (
        parseInt(configData?.configData?.max_power) <
          defaultsMinMax?.maxPowerBessMin ||
        parseInt(configData?.configData?.max_power) >
          maxPowerBessMax
      ) {
        callSnackbar(
          `error`,
          `${embLabels.maxPowerRange} ${embLabels.shouldBeInRangeOpen}${defaultsMinMax?.maxPowerBessMin}-${maxPowerBessMax}
           ${embLabels.kiloWatt}${embLabels.shouldBeInRangeClose}`
        );
      } else if (
        parseInt(configData?.configData?.capacity) <
          defaultsMinMax?.capacityBessMin ||
        parseInt(configData?.configData?.capacity) >
          capacityBessMax
      ) {
        callSnackbar(
          `error`,
          `${embLabels.capacity} ${embLabels.shouldBeInRangeOpen}${defaultsMinMax?.capacityBessMin}-${capacityBessMax}
           ${embLabels.kiloWattHour}${embLabels.shouldBeInRangeClose}`
        );
      } else if (
        parseInt(configData?.configData?.soc_max) < defaultsMinMax?.socMaxMin ||
        parseInt(configData?.configData?.soc_max) > defaultsMinMax?.socMaxMax
      ) {
        callSnackbar(
          `error`,
          `${embLabels.socMax} ${embLabels.shouldBeInRangeOpen}${defaultsMinMax?.socMaxMin}-${defaultsMinMax?.socMaxMax}
           ${embLabels.perentageOfMaxpower}${embLabels.shouldBeInRangeClose}`
        );
      } else if (
        parseInt(configData?.configData?.soc_min) < defaultsMinMax?.socMinMin ||
        parseInt(configData?.configData?.soc_min) > defaultsMinMax?.socMinMax
      ) {
        callSnackbar(
          `error`,
          `${embLabels.socMin} ${embLabels.shouldBeInRangeOpen}${defaultsMinMax?.socMinMin}-${defaultsMinMax?.socMinMax}
           ${embLabels.perentageOfMaxpower}${embLabels.shouldBeInRangeClose}`
        );
      } else {
        callSnackbar("error", embLabels.mandatoryFields);
      }
    }
  };

  const updateChpConfig = () => {
    if (
      checkForDuplicateName(
        configData?.configData?.name,
        CBAssetList[6]?.configData?.name
      )
    ) {
      callSnackbar(
        `error`,
        `"${configData?.configData?.name}" already exists, please provide an unique name"`
      );
    } else {
      if (
        /^[a-zA-Z0-9-_& ]+$/.test(configData?.configData?.name)  &&
        parseInt(configData?.configData?.nominal_power) >=
          defaultsMinMax?.chpNominalPowerMin &&
        parseInt(configData?.configData?.nominal_power) <=
          defaultsMinMax?.chpNominalPowerMax
      ) {
        CBAssetListDispatch({
          type: ACTIONS.UPDATE_ASSET_CONFIG,
          payload: {
            selectedAsset: selectedValues?.selectedAsset,
            configData: configData?.configData,
          },
        });
        if (duplicateUpdate.der === "") {
          setDuplicateUpdate({
            der: 6,
            load: "",
            pv: "",
            ev: "",
          });
        }

        updateDerHistory(6);
        updateDerStatus(7);

        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "CHP Configured Successfully");
      } else if (
        configData?.configData?.name === "" ||
        configData?.configData?.nominal_power.toString() === ""
      ) {
        callSnackbar("error", embLabels.mandatoryFields);
      // } else if (!isValidName(configData?.configData?.name)) {
      //   callSnackbar("error", embLabels.notallowedAlphanumericValues);
      } else if (
        parseInt(configData?.configData?.nominal_power) <
          defaultsMinMax?.chpNominalPowerMin ||
        parseInt(configData?.configData?.nominal_power) >
          defaultsMinMax?.chpNominalPowerMax
      ) {
        callSnackbar(
          `error`,
          `${embLabels.nominalPowerRange}${embLabels.shouldBeInRangeOpen}${defaultsMinMax?.chpNominalPowerMin}-${defaultsMinMax?.chpNominalPowerMax}${embLabels.kiloWatt}${embLabels.shouldBeInRangeClose}
      `
        );
      } else {
        callSnackbar("error", embLabels.mandatoryFields);
      }
    }
  };

  const updateHybridConfig = () => {
    if (
      checkForDuplicateName(
        configData?.configData?.name,
        CBAssetList?.[7]?.configData?.name
      )
    ) {
      callSnackbar(
        "error",
        `"${configData?.configData?.name}" already exists, please provide an unique name`
      );
    } else {
      if (
        /^[a-zA-Z0-9-_& ]+$/.test(configData?.configData?.name) &&
        parseInt(configData?.configData?.max_capacity) >=
          defaultsMinMax?.hybridMaxCapacityMin &&
        parseInt(configData?.configData?.max_capacity) <=
          defaultsMinMax?.hybridMaxCapacityMax &&
        parseInt(configData?.configData?.capacity) >=
          defaultsMinMax?.hybridCapacityMin &&
        parseInt(configData?.configData?.capacity) <=
          defaultsMinMax?.hybridCapacityMax &&
        parseInt(configData?.configData?.max_solar_power) >=
          defaultsMinMax?.hybridMaxSolarPowerMin &&
        parseInt(configData?.configData?.max_solar_power) <=
          defaultsMinMax?.hybridMaxSolarPowerMax &&
        parseInt(configData?.configData?.max_inverter_output_power) >=
          defaultsMinMax?.hybridInverterPowerMin &&
        parseInt(configData?.configData?.max_inverter_output_power) <=
          defaultsMinMax?.hybridInverterPowerMax
      ) {
        //A2E getset configuration
        const gesetAssetConfigDataNew = Object.assign(
          {},
          CBAssetList?.[1]?.configData
        );
        if (CBAssetList?.[1]?.assetType === embLabels.GENSET) {
          CBAssetList?.[1]?.selectedAssetData?.assetAttributes &&
            CBAssetList?.[1]?.selectedAssetData?.assetAttributes?.forEach(
              (assetAttribute) => {
                if (assetAttribute.type === "gensetPower") {
                  gesetAssetConfigDataNew.max_power = assetAttribute.value;
                }
              }
            );
        }
        CBAssetListDispatch({
          type: ACTIONS.UPDATE_ASSET_CONFIG,
          payload: {
            selectedAsset: 1,
            configData: gesetAssetConfigDataNew,
          },
        });
        const newConfigData = {...configData?.configData};
        const bessConfiguration = CBAssetList?.[selectedValues?.selectedAsset];
        newConfigData.model = bessConfiguration?.configData?.model;
        newConfigData.storage_capacity = bessConfiguration?.configData?.storage_capacity;
        newConfigData.corresponding_name_plate_capacity_value = bessConfiguration?.configData?.corresponding_name_plate_capacity_value;
        CBAssetListDispatch({
          type: ACTIONS.UPDATE_ASSET_CONFIG,
          payload: {
            selectedAsset: selectedValues?.selectedAsset,
            configData: newConfigData,
          },
        });
        if (duplicateUpdate.der === "") {
          setDuplicateUpdate({
            der: 1,
            load: "",
            pv: "",
            ev: "",
          });
        }

        updateDerHistory(9);
        updateDerStatus(9);

        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "Hybrid Configured Successfully");
      } else if (
        configData?.configData?.name.toString() === "" ||
        configData?.configData?.max_capacity?.toString() === "" ||
        configData?.configData?.capacity?.toString() === "" ||
        configData?.configData?.max_solar_power?.toString() === "" ||
        configData?.configData?.max_inverter_output_power.toString() === ""
      ) {
        callSnackbar("error", embLabels.mandatoryFields);
      // } else if (!isValidName(configData?.configData?.name)) {
      //   callSnackbar("error", embLabels.notallowedAlphanumericValues);
      } else if (
        parseInt(configData?.configData?.max_capacity) <
          defaultsMinMax?.maxPowerGensetMin ||
        parseInt(configData?.configData?.max_capacity) >
          defaultsMinMax?.hybridMaxCapacityMax
      ) {
        callSnackbar(
          `error`,
          `Max battery capacity should be in range (${defaultsMinMax?.hybridMaxCapacityMin}-${defaultsMinMax?.hybridMaxCapacityMax} kW DC)`
        );
      } else if (
        parseInt(configData?.configData?.capacity) <
          defaultsMinMax?.hybridCapacityMin ||
        parseInt(configData?.configData?.capacity) >
          defaultsMinMax?.hybridCapacityMax
      ) {
        callSnackbar(
          `error`,
          `Capacity should be in range (${defaultsMinMax?.hybridCapacityMin}-${defaultsMinMax?.hybridCapacityMax} KWh)`
        );
      } else if (
        parseInt(configData?.configData?.max_solar_power) <
          defaultsMinMax?.hybridMaxSolarPowerMin ||
        parseInt(configData?.configData?.max_solar_power) >
          defaultsMinMax?.hybridMaxSolarPowerMax
      ) {
        callSnackbar(
          `error`,
          `Max Inverter output power (${defaultsMinMax?.hybridMaxSolarPowerMin}-${defaultsMinMax?.hybridMaxSolarPowerMax} kw AC)`
        );
      } else if (
        parseInt(configData?.configData?.max_inverter_output_power) <
          defaultsMinMax?.hybridInverterPowerMin ||
        parseInt(configData?.configData?.max_inverter_output_power) >
          defaultsMinMax?.hybridInverterPowerMax
      ) {
        callSnackbar(
          `error`,
          `Max Inverter output power (${defaultsMinMax?.hybridInverterPowerMin}-${defaultsMinMax?.hybridInverterPowerMax} kw AC)`
        );
      } else {
        callSnackbar("error", embLabels.mandatoryFields);
      }
    }
  };

  const arrayMaxPowerFinder = () => {
    switch (parseInt(selectedValues?.selectedPv)) {
      case 3:
        return PVDetails?.pvset1Data.reduce(
          (prev, cur) => prev + +cur.max_power,
          0
        );
      case 4:
        return PVDetails?.pvset2Data.reduce(
          (prev, cur) => prev + +cur.max_power,
          0
        );
      case 5:
        return PVDetails?.pvset3Data.reduce(
          (prev, cur) => prev + +cur.max_power,
          0
        );
      default:
        return 0;
    }
  };

  const updatePvArrayConfig = () => {
    const index = PVDetails?.selectSubPv - 1;
    const selectedPv = PVDetails?.selectedPv;
    //  const isUniqueName = prevPvGroupNames.includes(PVDetails.pvConfig.groupName);
    // if (isUniqueName) {
    //    PVDetailsDispatch({
    //         type: ACTIONS.CONFIGURE_PV_ARRAY,
    //         payload: {
    //           data: prevPvGroupNames[index],
    //         },
    //       });
    //       //  CBAssetListDispatch({
    //       //       type: ACTIONS.CONFIGURE_UPDATE_DER_PV_ARRAY,
    //       //       payload: {
    //       //         data: e.target.value,
    //       //         selectedPvArray:selectedValues?.selectedPv
    //       //       },
    //       //     })
    //       //     assetEditStatusUpdatePv( 
    //       //       e,
    //       //       true,
    //       //       PVDetails.selectedPv,
    //       //       PVDetails.selectSubPv
    //       //     );
    //    callSnackbar(
    //     `error`,
    //     `"${PVDetails.pvConfig.groupName}" already exists, please provide an unique name`
    //   );
      
    //   return;
    // }
    if (
      /^[a-zA-Z0-9-_& ]+$/.test(PVDetails?.pvConfig?.groupName) &&
      arrayMaxPowerFinder() < parseInt(PVDetails?.pvConfig?.group_max_power)
    ) {
      callSnackbar(
        "error",
        `Array Max Power should not exceed sum of all pv inverter max power.`
      );
    } else if (
      PVDetails?.pvConfig?.groupName &&
      parseInt(PVDetails?.pvConfig?.group_max_power) >= 0
    ) {
      // if (isValidName(PVDetails?.pvConfig?.groupName)) {
        PVDetailsDispatch({
          type: ACTIONS.UPDATE_PV_ARRAY,
        });
        CBAssetListDispatch({
          type:ACTIONS.UPDATE_DER_DATA_PV_CONFIRGURED,
          payload: { PVDetails },
        })

        updatePvHistory(index, selectedPv);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "PV Array Configured Successfully");
      // } else {
      //   callSnackbar("error", embLabels.notallowedAlphanumericValues);
      // }
    } else {
      callSnackbar("error", embLabels.mandatoryFields);
    }
  };

  const updatePvConfig = () => {
    const projectType = projectTypeValue
    const pvNumber = PVDetails?.selectedPv;
    let whichSubPv = PVDetails?.selectSubPv - 1;
    let removeName;

    switch (pvNumber) {
      case 3:
        removeName = PVDetails?.pvset1Data[whichSubPv]?.name;
        break;
      case 4:
        removeName = PVDetails?.pvset2Data[whichSubPv]?.name;
        break;
      case 5:
        removeName = PVDetails?.pvset3Data[whichSubPv]?.name;
        break;
      default:
        break;
    }

    if (checkForDuplicateName(PVDetails?.pvConfig?.name, removeName)) {
      callSnackbar(
        `error`,
        `"${PVDetails?.pvConfig.name}" already exists, please provide an unique name`
      );
    } else {
      if (
       (( projectType === "DaaS ANSI"|| projectType === "DaaS ETO" )&& /^[a-z0-9-_]+$/.test(PVDetails?.pvConfig?.name)) &&
        parseInt(PVDetails?.pvConfig?.max_power) >=
          defaultsMinMax?.maxPowerPvMin &&
        parseInt(PVDetails?.pvConfig?.max_power) <= defaultsMinMax?.maxPowerPvMax
      ) {
        setDuplicateUpdate({
          der: "",
          load: "",
          pv: "pv",
          ev: "",
        });
        PVDetailsDispatch({
          type: ACTIONS.UPDATE_PV_CONFIG_SAVE,
        });
        PVDetailsDispatch({
          type: ACTIONS.PV_CONFIGURED_STATUS,
        });

        updatePvHistory(PVDetails?.selectSubPv - 1, PVDetails?.selectedPv);
        updatePvHistoryGroupPower(PVDetails?.selectedPv);

        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "PV Configured Successfully");
      }else if(( (projectType !== "DaaS ANSI" ||projectType !== "DaaS ETO") && PVDetails?.pvConfig?.name) &&
      parseInt(PVDetails?.pvConfig?.max_power) >=
        defaultsMinMax?.maxPowerPvMin &&
      parseInt(PVDetails?.pvConfig?.max_power) <= defaultsMinMax?.maxPowerPvMax){

        setDuplicateUpdate({
          der: "",
          load: "",
          pv: "pv",
          ev: "",
        });
        PVDetailsDispatch({
          type: ACTIONS.UPDATE_PV_CONFIG_SAVE,
        });
        PVDetailsDispatch({
          type: ACTIONS.PV_CONFIGURED_STATUS,
        });

        updatePvHistory(PVDetails?.selectSubPv - 1, PVDetails?.selectedPv);
        updatePvHistoryGroupPower(PVDetails?.selectedPv);

        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "PV Configured Successfully");
      } 
       else if (
        PVDetails?.pvConfig?.name.toString() === "" ||
        PVDetails?.pvConfig?.max_power.toString() === ""
      ) {
        callSnackbar("error", embLabels.mandatoryFields);
      // } else if (!isValidName(PVDetails.pvConfig.name)) {
      //   callSnackbar("error", embLabels.notallowedAlphanumericValues);
      } else if (
        parseInt(PVDetails?.pvConfig?.max_power) < defaultsMinMax?.maxPowerPvMin ||
        parseInt(PVDetails?.pvConfig?.max_power) > defaultsMinMax?.maxPowerPvMax
      ) {
        callSnackbar(
          `error`,
          `Max Power should be in range(${defaultsMinMax?.maxPowerPvMin}-${defaultsMinMax?.maxPowerPvMax} kW)`
        );
      } else {
        callSnackbar("error", embLabels.mandatoryFields);
      }
    }
  };

  const updateLoadConfig = () => {
    const [powerIsBetween, nominalPowerMin, nominalPowerMax] =
      calculateMaxAvailableLoadPower();

    if (
      checkForDuplicateName(
        configData?.configData?.name,
        CBLoadList?.[selectedValues?.selectedLoad]?.configData?.name
      )
    ) {
      callSnackbar(
        `error`,
        `"${configData?.configData?.name}" already exists, please provide an unique name"`
      );
    }else if( CBLoadList?.[selectedValues?.selectedLoad]?.uiData?.contactor && configData?.contactorConfigData?.name === ""){
       callSnackbar("error", embLabels.mandatoryFields);
    } else {
      if (
        /^[a-zA-Z0-9-_& ]+$/.test(configData?.configData?.name) &&
        configData?.configData?.maximum_available_power?.toString().trim() &&
        powerIsBetween
      ) {
        CBLoadListDispatch({
          type: ACTIONS.UPDATE_LOAD_CONFIG,
          payload: {
            selectedLoad: selectedValues?.selectedLoad,
            configData: configData?.configData,
          },
        });

        setDuplicateUpdate({
          der: "",
          load: selectedValues?.selectedLoad,
          pv: "",
          ev: "",
        });

        updateLoadHistory(selectedValues?.selectedLoad);
         if ( /^[a-zA-Z0-9-_& ]+$/.test(configData?.cbConfigData?.name)) {
           updateLoadCbConfigData();
           setIsOpenLoadConfig(false);
        } else {
         callSnackbar("error", embLabels.mandatoryFields);
         return;
       }
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        setIsOpenLoadConfig(false);
        // callSnackbar("information", "Load Configured Successfully");
      } else if (
        configData?.configData?.name === "" ||
        configData?.configData?.maximum_available_power?.toString() === ""
      ) {
        callSnackbar("error", embLabels.mandatoryFields);
      // } else if (!isValidName(configData?.configData?.name)) {
      //   callSnackbar("error", embLabels.notallowedAlphanumericValues);
      } else if (!powerIsBetween) {
        callSnackbar(
          `error`,
          `Maximum Available Power should be in range (${nominalPowerMin}-${nominalPowerMax} kW)`
        );
      } else {
        callSnackbar("error", embLabels.mandatoryFields);
      }
    }
    checkAssetStatus();
  };
  

  const updateEvConfig = () => {
    if (
      checkForDuplicateName(
        configData?.configData?.name,
        CBEVList[0]?.configData?.name
      )
    ) {
      callSnackbar(
        `error`,
        `"${configData?.configData?.name}" already exists, please provide an unique name`
      );
    } else {
      if (
        /^[a-zA-Z0-9-_& ]+$/.test(configData?.configData?.name) &&
        parseInt(configData?.configData?.maximum_available_power) >=
          defaultsMinMax?.maxPowerEvMin &&
        parseInt(configData?.configData?.maximum_available_power) <=
          defaultsMinMax?.maxPowerEvMax
      ) {
        CBEVListDispatch({
          type: ACTIONS.UPDATE_EV_CONFIG,
          payload: {
            selectedEv: selectedValues.selectedEv,
            configData: configData.configData,
            isEvce,
            defaultValues: defaultPreFillData[0]
          },
        });
        setDuplicateUpdate({
          der: "",
          load: "",
          pv: "",
          ev: selectedValues?.selectedEv,
        });
        updateDerHistory(5);
        updateDerStatus(5);

        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "EV Configured Successfully");
      } else if (
        configData?.configData?.name === "" ||
        configData?.configData?.maximum_available_power.toString() === ""
      ) {
        callSnackbar("error", embLabels.mandatoryFields);
      // } else if (!isValidName(configData?.configData?.name)) {
      //   callSnackbar("error", embLabels.notallowedAlphanumericValues);
      } else if (
        parseInt(configData?.configData?.maximum_available_power) <
          defaultsMinMax?.maxPowerEvMin ||
        parseInt(configData?.configData?.maximum_available_power) >
          defaultsMinMax?.maxPowerEvMax
      ) {
        callSnackbar(
          `error`,
          `Maximum Available Power should be in range (${defaultsMinMax?.maxPowerEvMin}-${defaultsMinMax?.maxPowerEvMax} kW)`
        );
      } else {
        callSnackbar("error", embLabels.mandatoryFields);
      }
    }
  };
  const updateGtConfig = () => {
     const cbConfigData = CBGTList[0]?.cbConfigData;
     if(cbConfigData.name === ""){
      callSnackbar("error", embLabels.mandatoryFields);
      return null;
     }
     if ( /^[a-zA-Z0-9-_& ]+$/.test(configData?.gtConfigData?.name)) {
        CBGTListDispatch({
          type: ACTIONS.UPDATE_GT_CONFIG,
          payload: {
            selectedGt: selectedValues.selectedGt,
            configData: configData,
            defaultValues: defaultPreFillData[0]
          },
        });
        setDuplicateUpdate({
          der: "",
          load: "",
          pv: "",
          gt: selectedValues.selectedGt,
        });
        updateDerHistory(10);
        updateDerStatus(10);

        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        setIsOpenGtConfig(false);
        callSnackbar("information", "GT Configured Successfully");
      } else if ( configData.gtConfigData.name === "") {
        callSnackbar("error", embLabels.mandatoryFields);
      } 
    
  };

  const updateCbConfigData = () => {
    const projectType = projectTypeValue
    if (/^[a-z0-9-_]+$/.test(configData?.cbConfigData?.name) && (projectType === "DaaS ANSI" || projectType === "DaaS ETO")) {
      CBAssetListDispatch({
        type: ACTIONS.UPDATE_CB_CONFIG,
        payload: {
          selectedAssetCB: selectedValues?.selectedAssetCB,
          cbConfigData: configData?.cbConfigData,
        },
      });
      if (
        configData.contactorConfigData.name &&
        [embLabels.GRID_CONNECTED,embLabels.ISLANDABLE,embLabels.OFF_GRID].includes(siteType) &&
        clientType === embLabels.GENERIC_PROJECT
        &&
        CBAssetList[selectedValues?.selectedAssetCB]?.uiData?.contactor
      ) {
        CBAssetListDispatch({
          type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG,
          payload: {
            selectedAssetCB: selectedValues?.selectedAssetCB,
            cbConfigData: configData?.contactorConfigData,
            checked: CBAssetList[selectedValues?.selectedAssetCB]?.uiData?.contactor
          },
        });
      }
      if(configData?.cbConfigData.contactor&&configData?.contactorConfigData.name&&configData?.cbConfigData.name){
        updateAssetCbHistory(selectedValues?.selectedAssetCB);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "Circuit Breaker Configured Successfully");
      }else if(configData?.cbConfigData.motorized&&configData?.cbConfigData.name){
        updateAssetCbHistory(selectedValues?.selectedAssetCB);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "Circuit Breaker Configured Successfully");
      }else if(configData?.cbConfigData.name){
        updateAssetCbHistory(selectedValues?.selectedAssetCB);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "Circuit Breaker Configured Successfully");
      }else{
        callSnackbar(`error`,embLabels.mandatoryFields);
      }
    }else if (/^[a-zA-Z0-9-_& ]+$/.test(configData?.cbConfigData?.name) &&( projectType !== "DaaS ANSI" || projectType !== "DaaS ETO")) {
      if(configData?.cbConfigData?.contactor && configData?.contactorConfigData?.name === ""){
        callSnackbar(`error`,embLabels.mandatoryFields);
        return ;
      }
      CBAssetListDispatch({
        type: ACTIONS.UPDATE_CB_CONFIG,
        payload: {
          selectedAssetCB: selectedValues?.selectedAssetCB,
          cbConfigData: configData?.cbConfigData,
        },
      });
      if (
        configData?.contactorConfigData?.name &&
        [embLabels.GRID_CONNECTED,embLabels.ISLANDABLE, embLabels.OFF_GRID].includes(siteType) &&
        clientType === embLabels.GENERIC_PROJECT
        &&
        CBAssetList?.[selectedValues?.selectedAssetCB]?.uiData?.contactor
      ) {
        CBAssetListDispatch({
          type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG,
          payload: {
            selectedAssetCB: selectedValues?.selectedAssetCB,
            cbConfigData: configData?.contactorConfigData,
            checked: CBAssetList?.[selectedValues?.selectedAssetCB]?.uiData?.contactor
          },
        });
      }
      if(configData?.cbConfigData?.contactor&&configData?.contactorConfigData?.name&&configData?.cbConfigData?.name){
        updateAssetCbHistory(selectedValues?.selectedAssetCB);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "Circuit Breaker Configured Successfully");
      }else if(configData?.cbConfigData?.motorized&&configData?.cbConfigData?.name){
        updateAssetCbHistory(selectedValues?.selectedAssetCB);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "Circuit Breaker Configured Successfully");
      }
      else if(!configData?.cbConfigData?.contactor && !configData?.cbConfigData?.motorized){
        updateAssetCbHistory(selectedValues?.selectedAssetCB);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "Circuit Breaker Configured Successfully");
      }
      else{
        callSnackbar(`error`,embLabels.mandatoryFields);
      }
    } else {
      callSnackbar("error", embLabels.mandatoryFields);
    }
  };

  const updateCbContactorConfigData = () => {
    if (configData?.contactorConfigData?.name) {
      CBAssetListDispatch({
        type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG,
        payload: {
          selectedAssetCB: selectedValues?.selectedAssetCB,
          cbConfigData: configData?.contactorConfigData,
        },
      });

      updateAssetCbHistory(selectedValues?.selectedAssetCB);

      updateCbConfigData();
    } else {
      callSnackbar("error", embLabels.mandatoryFields);
    }
  };

  const updateEvCbConfigData = () => {
    if (/^[a-zA-Z0-9-_& ]+$/.test(configData?.cbConfigData?.name)) {
      CBEVListDispatch({
        type: ACTIONS.UPDATE_EV_CB_CONFIG,
        payload: {
          selectedEvCB: selectedValues?.selectedEvCB,
          cbConfigData: configData?.cbConfigData,
        },
      });
      if (
        configData?.contactorConfigData.name 
      ) {
        CBEVListDispatch({
          type: ACTIONS.UPDATE_CB_CONTACTOR_CONFIG_DATA,
          payload: {
            index: selectedValues?.selectedEvCB,
            CBContactor: configData?.contactorConfigData,
            defaultContactor: configData?.contactorConfigData,
            checked: CBEVList[selectedValues?.selectedEvCB]?.uiData?.contactor,
          },
        });
      }
      if(configData?.cbConfigData?.contactor&&configData?.contactorConfigData?.name&&configData?.cbConfigData?.name){
        updateAssetCbHistory(selectedValues?.selectedAssetCB);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        callSnackbar("information", "Circuit Breaker Configured Successfully");
      }else if(configData?.cbConfigData?.motorized&&configData?.cbConfigData?.name){
        updateAssetCbHistory(selectedValues?.selectedAssetCB);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        callSnackbar("information", "Circuit Breaker Configured Successfully");
      }else if(configData?.cbConfigData?.name){
        updateAssetCbHistory(selectedValues?.selectedAssetCB);
        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
        sessionStorage.setItem("componentDataModified",true);
        callSnackbar("information", "Circuit Breaker Configured Successfully");
      }else{
        callSnackbar(`error`,embLabels.mandatoryFields);
      }
    } else {
      callSnackbar("error", embLabels.mandatoryFields);
    }
  };

  const updateLoadCbConfigData = () => {
    if ( /^[a-zA-Z0-9-_& ]+$/.test(configData?.cbConfigData?.name)) {
      CBLoadListDispatch({
        type: ACTIONS.UPDATE_LOAD_CB_CONFIG,
        payload: {
          selectedLoadCB: selectedValues?.selectedLoad,
          cbConfigData: configData?.cbConfigData,
        },
      });

      updateLoadCbHistory(selectedValues?.selectedLoad);

      const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
      setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
      sessionStorage.setItem("componentDataModified",true);
      
      // callSnackbar("information", "Circuit Breaker Configured Successfully");
      callSnackbar("information", " Load Configured Successfully");

     
    } else {
      callSnackbar("error", embLabels.mandatoryFields);
   
    }
  };
  const updateRemoteLoadCbConfigData = () => {
    if ( /^[a-zA-Z0-9-_& ]+$/.test(configData?.cbConfigData?.name)) {
      CBLoadListDispatch({
        type: ACTIONS.UPDATE_LOAD_CB_CONFIG,
        payload: {
          selectedLoadCB: selectedValues?.selectedLoadCB,
          cbConfigData: configData?.cbConfigData,
        },
      });

      updateLoadCbHistory(selectedValues?.selectedLoadCB);

      const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
      setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
      sessionStorage.setItem("componentDataModified",true);
      // callSnackbar("information", "Circuit Breaker Configured Successfully");
      callSnackbar("information", " Load Configured Successfully");

     
    } else {
      callSnackbar("error", embLabels.mandatoryFields);
    }
  };
  const updatePowerMeterData = () => {
    const projectType = projectTypeValue
    if(/^[a-z0-9-_]+$/.test(configData?.cbConfigData?.name) &&( projectType === "DaaS ANSI" ||  projectType === "DaaS ETO")){
      if (selectedValues?.selectedAssetPM !== "") {
        if (detailsBlock.existingPmAssetDetailsBlockGrid) {
          CBAssetListDispatch({
            type: ACTIONS.UPDATE_EXISTING_POWERMETER_CONFIG,
            payload: {
              selectedAssetPM: selectedValues?.selectedAssetPM,
              cbConfigData: configData?.cbConfigData,
            },
          });
        } else {
          CBAssetListDispatch({
            type: ACTIONS.UPDATE_POWERMETER_CONFIG,
            payload: {
              selectedAssetPM: selectedValues?.selectedAssetPM,
              cbConfigData: configData?.cbConfigData,
            },
          });
        }

        updateAssetPmHistory(selectedValues?.selectedAssetPM);
      }
      if (selectedValues?.selectedEvPM !== "") {
        CBEVListDispatch({
          type: ACTIONS.UPDATE_EV_POWERMETER_CONFIG,
          payload: {
            selectedAssetPM: selectedValues?.selectedEvPM,
            cbConfigData: configData?.cbConfigData,
          },
        });

        updateEvPmHistory(selectedValues?.selectedEvPM);
      }

      if(selectedValues?.selectedLoadPM!=="" ){
        CBLoadListDispatch({
          type: ACTIONS.UPDATE_LOAD_POWERMETER_CONFIG,
          payload: {
            selectedAssetPM: selectedValues?.selectedLoadPM,
            cbConfigData: configData?.cbConfigData,
          },
        });
      }

      const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
      setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
      sessionStorage.setItem("componentDataModified",true);
      callSnackbar("information", "Power Meter Configured Successfully");
    }else if ( /^[a-zA-Z0-9-_& ]+$/.test(configData?.cbConfigData?.name) && (projectType !== "DaaS ANSI" || projectType !== "DaaS ETO")) {
      if (selectedValues?.selectedAssetPM !== "") {
        if (detailsBlock.existingPmAssetDetailsBlockGrid) {
          CBAssetListDispatch({
            type: ACTIONS.UPDATE_EXISTING_POWERMETER_CONFIG,
            payload: {
              selectedAssetPM: selectedValues?.selectedAssetPM,
              cbConfigData: configData?.cbConfigData,
            },
          });
        } else {
          CBAssetListDispatch({
            type: ACTIONS.UPDATE_POWERMETER_CONFIG,
            payload: {
              selectedAssetPM: selectedValues?.selectedAssetPM,
              cbConfigData: configData?.cbConfigData,
            },
          });
        }

        updateAssetPmHistory(selectedValues?.selectedAssetPM);
      }
      if (selectedValues?.selectedEvPM !== "") {
        CBEVListDispatch({
          type: ACTIONS.UPDATE_EV_POWERMETER_CONFIG,
          payload: {
            selectedAssetPM: selectedValues?.selectedEvPM,
            cbConfigData: configData?.cbConfigData,
          },
        });

        updateEvPmHistory(selectedValues?.selectedEvPM);
      }

      if(selectedValues?.selectedLoad!==""){
        CBLoadListDispatch({
          type: ACTIONS.UPDATE_LOAD_POWERMETER_CONFIG,
          payload: {
            selectedAssetPM: selectedValues?.selectedLoad,
            cbConfigData: configData?.cbConfigData,
          },
        });
      } else if(selectedValues?.selectedLoadPM!=="" ){
        CBLoadListDispatch({
          type: ACTIONS.UPDATE_LOAD_POWERMETER_CONFIG,
          payload: {
            selectedAssetPM: selectedValues?.selectedLoadPM,
            cbConfigData: configData?.cbConfigData,
          },
        });
      }


      const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
      setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);
      sessionStorage.setItem("componentDataModified",true);
      callSnackbar("information", "Power Meter Configured Successfully");
    } else {
      callSnackbar("error", embLabels.mandatoryFields);
    }
  };

  const updateLoadCbPm = ()=>{
    if(CBLoadList[selectedValues?.selectedLoad]?.uiData?.contactor && CBLoadList[selectedValues?.selectedLoad]?.contactorConfigData?.name ==="") {
      callSnackbar(`error`,"Contactor Name is mandatory for contactor type circuit breaker");
      return;
    }
    updateLoadConfig(); 
    // setIsOpenLoadConfig(false);
  }
  const updateGtCb = ()=>{
    if(CBGTList[selectedValues?.selectedGt]?.uiData?.contactor && CBGTList[selectedValues?.selectedGt]?.contactorConfigData?.name ==="") {
      callSnackbar(`error`,"Contactor Name is mandatory for contactor type circuit breaker");
      return;
    }
    updateGtConfig(); 
    // setIsOpenLoadConfig(false);
  }
  const pv1SelectHandler = () => {
    if (UserScope.access.saveSLD) {
      assetDetailsReset();
      const assetBlock = "assetPvDetailsBlock";
      const selectedAssetValue = "selectedPv";
      CBAssetListDispatch({
        type: ACTIONS.ASSET_SELECT,
        payload: {
          index: 3,
        },
      });
      selectedValuesHandler(3, selectedAssetValue, assetBlock);
      selectedSubPvHandler(1);
      PVDetailsDispatch({
        type: ACTIONS.PV_SELECT,
        payload: {
          selectedPv: 3,
          selectSubPv: 1,
          externalId: gsxExternalId?.pvAssetExternalId["4"],
        },
      });
      selectedAssetHandlerPv(3, 1);
      PVDetailsDispatch({
        type: ACTIONS.PV_CONFIGURED_STATUS,
      });
      PVDetailsDispatch({
        type: ACTIONS.PV_CONFIG_DATA,
      });
    } else {
      callSnackbar(
        `error`,
        `${UserScope.role} is not authorized to perform action on this page`
      );
    }
  };

  const pv2SelectHandler = () => {
    if (UserScope.access.saveSLD) {
      assetDetailsReset();
      const assetBlock = "assetPvDetailsBlock";
      const selectedAssetValue = "selectedPv";
      CBAssetListDispatch({
        type: ACTIONS.ASSET_SELECT,
        payload: {
          index: 4,
        },
      });
      selectedValuesHandler(4, selectedAssetValue, assetBlock);
      selectedSubPvHandler(1);
      PVDetailsDispatch({
        type: ACTIONS.PV_SELECT,
        payload: {
          selectedPv: 4,
          selectSubPv: 1,
        },
      });
      selectedAssetHandlerPv(4, 1);
      PVDetailsDispatch({
        type: ACTIONS.PV_CONFIGURED_STATUS,
      });
      PVDetailsDispatch({
        type: ACTIONS.PV_CONFIG_DATA,
      });
    } else {
      callSnackbar(
        `error`,
        `${UserScope.role} is not authorized to perform action on this page`
      );
    }
  };

  const pv3SelectHandler = () => {
    if (UserScope.access.saveSLD) {
      assetDetailsReset();
      const assetBlock = "assetPvDetailsBlock";
      const selectedAssetValue = "selectedPv";
      CBAssetListDispatch({
        type: ACTIONS.ASSET_SELECT,
        payload: {
          index: 5,
        },
      });
      selectedValuesHandler(5, selectedAssetValue, assetBlock);
      selectedSubPvHandler(1);
      PVDetailsDispatch({
        type: ACTIONS.PV_SELECT,
        payload: {
          selectedPv: 5,
          selectSubPv: 1,
        },
      });
      selectedAssetHandlerPv(5, 1);
      PVDetailsDispatch({
        type: ACTIONS.PV_CONFIGURED_STATUS,
      });
      PVDetailsDispatch({
        type: ACTIONS.PV_CONFIG_DATA,
      });
    } else {
      callSnackbar(
        `error`,
        `${UserScope.role} is not authorized to perform action on this page`
      );
    }
  };


  return {
    assetConfigDataHandler,
    loadConfigDataHandler,
    gtConfigDataHandler,
    evConfigDataHandler,
    cbConfigDataHandler,
    newSBCbConfigDataHandler,
    loadCbConfigDataHandler,
    gtCbConfigDataHandler,
    evCbConfigDataHandler,
    pmConfigDataHandler,
    existingPmConfigDataHandler,
    loadpmConfigDataHandler,
    evpmConfigDataHandler,
    handleUtilityChange,
    handleGensetChange,
    handleBatteryChange,
    handleChpChange,
    handleHybridChange,
    handlePvChange,
    handleLoadChange,
    handleGtChange,
    handleEvChange,
    handleCBConfigChange,
    handleCBContactorConfigChange,
    handleCBContactorConfigChangeLoad,
    handleCBConfigChangeLoad,
    handleGtCbConfigChange,
    handleCBConfigChangeEv,
    calculateMaxAvailableUtilityPower,
    calculateMaxAvailableLoadPower,
    calculateMaxAvailableGensetPower,
    updateUtilitytConfig,
    updateGensetConfig,
    updateBatteryConfig,
    updateChpConfig,
    updateHybridConfig,
    arrayMaxPowerFinder,
    updatePvArrayConfig,
    updatePvConfig,
    updateLoadConfig,
    updateEvConfig,
    updateGtConfig,
    updateCbConfigData,
    updateCbContactorConfigData,
    updateEvCbConfigData,
    updateLoadCbConfigData,
    updateRemoteLoadCbConfigData,
    updatePowerMeterData,
    updateLoadCbPm,
    updateGtCb,
    pv1SelectHandler,
    pv2SelectHandler,
    pv3SelectHandler,
  };
};

export default useAssetConfigHandlers;